// Dewey Decimal Classification — human-readable names for every number.
// Covers Level 2 (100 divisions), Level 3 (~1000 sections),
// and Level 4–6 sub-sections for major academic disciplines.
// No DDC numbers are ever displayed to users — this file is the lookup only.

const DDC_NAMES = {

  // ═══════════════════════════════════════════════════════════
  // LEVEL 1 — MAIN CLASSES (single digit, 0–9)
  // These must be DIFFERENT from the '000'–'900' division names
  // so that the X00 chip at the main-class level shows a proper
  // label rather than "Further sub-topics".
  // ═══════════════════════════════════════════════════════════

  '0': 'Computer Science & General Works',
  '1': 'Philosophy & Psychology',
  '2': 'Religion',
  '3': 'Social Sciences',
  '4': 'Language',
  '5': 'Natural Sciences & Mathematics',
  '6': 'Technology & Applied Sciences',
  '7': 'Arts & Recreation',
  '8': 'Literature',
  '9': 'History & Geography',

  // ═══════════════════════════════════════════════════════════
  // LEVEL 2 — DIVISIONS (tens)
  // X00 entries are given SPECIFIC names so they differ from the
  // single-digit parent above (prevents "Further sub-topics").
  // ═══════════════════════════════════════════════════════════

  '000': 'Computer Science, AI & Reference',
  '010': 'Bibliographies',
  '020': 'Library & Information Science',
  '030': 'General Encyclopedias',
  '040': 'Unassigned',
  '050': 'General Serial Publications',
  '060': 'General Organizations & Museums',
  '070': 'Journalism, News Media & Publishing',
  '080': 'General Collections',
  '090': 'Manuscripts & Rare Books',
  '100': 'Philosophy of Knowledge & Mind',
  '110': 'Metaphysics',
  '120': 'Epistemology & Knowledge',
  '130': 'Parapsychology & Occultism',
  '140': 'Philosophical Schools of Thought',
  '150': 'Psychology',
  '160': 'Logic',
  '170': 'Ethics & Moral Philosophy',
  '180': 'Ancient, Medieval & Eastern Philosophy',
  '190': 'Modern Western Philosophy',
  '200': 'World Religions & Theology',
  '210': 'Philosophy & Theory of Religion',
  '220': 'Bible',
  '230': 'Christianity & Christian Theology',
  '240': 'Christian Practice & Observance',
  '250': 'Christian Pastoral Tradition',
  '260': 'Christian Organization & Work',
  '270': 'History of Christianity',
  '280': 'Christian Denominations & Sects',
  '290': 'Other Religions',
  '300': 'Society, Knowledge & Social Theory',
  '310': 'Statistics',
  '320': 'Political Science',
  '330': 'Economics',
  '340': 'Law',
  '350': 'Public Administration & Military Science',
  '360': 'Social Problems & Social Services',
  '370': 'Education',
  '380': 'Commerce, Communications & Transportation',
  '390': 'Customs, Etiquette & Folklore',
  '400': 'General Language & Linguistics',
  '410': 'Linguistics',
  '420': 'English Language',
  '430': 'German Language',
  '440': 'French Language',
  '450': 'Italian Language',
  '460': 'Spanish & Portuguese Languages',
  '470': 'Latin Language',
  '480': 'Classical Greek Language',
  '490': 'Other Languages',
  '500': 'Natural Sciences — General Works',
  '510': 'Mathematics',
  '520': 'Astronomy',
  '530': 'Physics',
  '540': 'Chemistry',
  '550': 'Earth Sciences & Geology',
  '560': 'Paleontology & Paleozoology',
  '570': 'Biology',
  '580': 'Plants (Botany)',
  '590': 'Animals (Zoology)',
  '600': 'Technology — General Works',
  '610': 'Medicine & Health',
  '620': 'Engineering',
  '630': 'Agriculture',
  '640': 'Home Economics & Family Living',
  '650': 'Management & Business',
  '660': 'Chemical Engineering',
  '670': 'Manufacturing',
  '680': 'Manufacture for Specific Uses',
  '690': 'Building & Construction',
  '700': 'Fine Arts — General Works & Philosophy',
  '710': 'Landscape & Area Planning',
  '720': 'Architecture',
  '730': 'Sculpture & Related Arts',
  '740': 'Drawing & Decorative Arts',
  '750': 'Painting',
  '760': 'Printmaking & Prints',
  '770': 'Photography',
  '780': 'Music',
  '790': 'Sports, Games & Entertainment',
  '800': 'Literary Criticism, Rhetoric & Collections',
  '810': 'American Literature in English',
  '820': 'English Literature',
  '830': 'German Literature',
  '840': 'French Literature',
  '850': 'Italian Literature',
  '860': 'Spanish & Portuguese Literature',
  '870': 'Latin Literature',
  '880': 'Classical Greek Literature',
  '890': 'Other Literatures',
  '900': 'World History & Biography (General)',
  '910': 'Geography & Travel',
  '920': 'Biography & Genealogy',
  '930': 'History of Ancient World',
  '940': 'History of Europe',
  '950': 'History of Asia',
  '960': 'History of Africa',
  '970': 'History of North America',
  '980': 'History of South America',
  '990': 'History of Oceania & Pacific Islands',

  // ═══════════════════════════════════════════════════════════
  // LEVEL 3 — SECTIONS (three-digit numbers)
  // ═══════════════════════════════════════════════════════════

  // 000s — Computer Science & General Knowledge
  '001': 'Knowledge & Learning',
  '002': 'The Book',
  '003': 'Systems',
  '004': 'Data Processing & Computer Science',
  '005': 'Computer Software & Programming',
  '006': 'Special Computer Methods',
  '007': 'Unassigned',
  '008': 'Unassigned',
  '009': 'Unassigned',
  '011': 'General Bibliographies & Catalogs',
  '012': 'Bibliographies of Individuals',
  '013': 'Bibliographies of Works by Specific Groups',
  '014': 'Bibliographies of Anonymous Works',
  '015': 'Bibliographies by Place',
  '016': 'Bibliographies by Subject',
  '017': 'General Subject Catalogs',
  '018': 'Catalogs by Author & Date',
  '019': 'Dictionary Catalogs',
  '021': 'Library Relationships & Cooperation',
  '022': 'Library Physical Plant Administration',
  '023': 'Library Personnel Administration',
  '025': 'Library Operations & Services',
  '026': 'Libraries for Specific Subjects',
  '027': 'General Libraries',
  '028': 'Reading & Use of Media',
  '031': 'General Encyclopedias in English',
  '032': 'General Encyclopedias in English (British)',
  '039': 'General Encyclopedias in Other Languages',
  '051': 'General Periodicals in English',
  '059': 'General Periodicals in Other Languages',
  '061': 'Organizations in North America',
  '069': 'Museum Science',
  '071': 'Newspapers & Journalism in North America',
  '079': 'Newspapers & Journalism Elsewhere',
  '081': 'General Collections in English',
  '089': 'General Collections in Other Languages',
  '091': 'Manuscripts',
  '092': 'Block Books',
  '093': 'Incunabula',
  '094': 'Printed Books',
  '095': 'Books Notable for Bindings',
  '096': 'Books Notable for Illustrations',
  '097': 'Books Notable for Ownership',
  '098': 'Prohibited Works, Forgeries & Hoaxes',
  '099': 'Books Notable for Format',

  // 100s — Philosophy
  '101': 'Theory of Philosophy',
  '103': 'Dictionaries of Philosophy',
  '107': 'Education & Research in Philosophy',
  '108': 'Philosophy by Groups of People',
  '109': 'History of Philosophy',
  '111': 'Ontology',
  '113': 'Cosmology',
  '114': 'Space',
  '115': 'Time',
  '116': 'Change',
  '117': 'Structure',
  '118': 'Force & Energy',
  '119': 'Number & Quantity',
  '121': 'Epistemology',
  '122': 'Causation',
  '123': 'Determinism & Indeterminism',
  '124': 'Teleology',
  '126': 'The Self',
  '127': 'The Unconscious & Subconscious',
  '128': 'Humankind',
  '129': 'Origin & Destiny of the Soul',
  '131': 'Parapsychological Methods',
  '133': 'Paranormal Phenomena',
  '135': 'Dreams, Mysteries & Astrology',
  '141': 'Idealism & Related Systems',
  '142': 'Critical Philosophy',
  '143': 'Bergsonism & Intuitionism',
  '144': 'Humanism',
  '146': 'Naturalism',
  '147': 'Pantheism',
  '149': 'Other Philosophical Systems',
  '152': 'Sensation, Perception & Emotions',
  '153': 'Conscious Mental Processes & Intelligence',
  '154': 'Subconscious & Altered States',
  '155': 'Differential & Developmental Psychology',
  '156': 'Comparative Psychology',
  '158': 'Applied Psychology',
  '161': 'Inductive Logic',
  '162': 'Deductive Logic',
  '165': 'Fallacies & Errors in Reasoning',
  '166': 'Syllogisms',
  '168': 'Argument & Persuasion',
  '171': 'Ethical Theories',
  '172': 'Political Ethics',
  '173': 'Ethics of Family Relationships',
  '174': 'Occupational Ethics',
  '175': 'Ethics of Recreation',
  '176': 'Ethics of Sex & Reproduction',
  '177': 'Ethics of Social Relations',
  '178': 'Ethics of Consumption',
  '179': 'Other Ethical Norms',
  '181': 'Eastern Philosophy',
  '182': 'Pre-Socratic Philosophy',
  '184': 'Platonic Philosophy',
  '185': 'Aristotelian Philosophy',
  '186': 'Skeptic & Neoplatonic Philosophies',
  '187': 'Epicurean Philosophy',
  '188': 'Stoic Philosophy',
  '189': 'Medieval Western Philosophy',
  '191': 'Philosophy of the United States & Canada',
  '192': 'British Philosophy',
  '193': 'German & Austrian Philosophy',
  '194': 'French Philosophy',
  '195': 'Italian Philosophy',
  '196': 'Spanish & Portuguese Philosophy',
  '197': 'Russian Philosophy',
  '198': 'Scandinavian Philosophy',
  '199': 'Philosophy of Other Areas',

  // 200s — Religion
  '201': 'Religious Mythology & Social Theology',
  '202': 'Doctrines',
  '203': 'Public Worship & Other Practices',
  '204': 'Religious Experience, Life & Practice',
  '205': 'Religious Ethics',
  '206': 'Religious Leaders & Organization',
  '207': 'Missions & Religious Education',
  '208': 'Sources & Holy Texts',
  '209': 'Sects & Reform Movements',
  '211': 'Concepts of God',
  '212': 'Existence, Knowability & Attributes of God',
  '213': 'Creation',
  '214': 'Theodicy',
  '215': 'Science & Religion',
  '218': 'Humankind in Relation to God',
  '221': 'Old Testament',
  '222': 'Historical Books of Old Testament',
  '223': 'Poetic Books of Old Testament',
  '224': 'Prophetic Books of Old Testament',
  '225': 'New Testament',
  '226': 'Gospels & Acts',
  '227': 'Epistles',
  '228': 'Revelation',
  '229': 'Apocrypha',
  '231': 'God & Attributes',
  '232': 'Jesus Christ & His Family',
  '233': 'Humankind',
  '234': 'Salvation & Grace',
  '235': 'Spiritual Beings',
  '236': 'Eschatology',
  '238': 'Creeds & Catechisms',
  '239': 'Apologetics',
  '241': 'Christian Ethics & Moral Theology',
  '242': 'Devotional Literature',
  '243': 'Evangelistic Writings',
  '246': 'Use of Art in Christianity',
  '247': 'Church Furnishings & Articles',
  '248': 'Christian Experience & Practice',
  '249': 'Christian Observances in Family Life',
  '251': 'Preaching',
  '252': 'Texts of Sermons',
  '253': 'Pastoral Office & Work',
  '254': 'Parish Administration',
  '255': 'Religious Congregations',
  '259': 'Activities of the Church',
  '261': 'Social Theology',
  '262': 'Ecclesiology',
  '263': 'Days, Times & Places',
  '264': 'Public Worship',
  '265': 'Sacraments, Other Rites & Acts',
  '266': 'Missions',
  '267': 'Associations for Religious Work',
  '268': 'Religious Education',
  '269': 'Spiritual Renewal',
  '271': 'Religious Orders',
  '272': 'Persecutions',
  '273': 'Heresies',
  '274': 'Christian Church in Europe',
  '275': 'Christian Church in Asia',
  '276': 'Christian Church in Africa',
  '277': 'Christian Church in North America',
  '278': 'Christian Church in South America',
  '279': 'Christian Church in Other Areas',
  '281': 'Early Church & Eastern Churches',
  '282': 'Roman Catholic Church',
  '283': 'Anglican Churches',
  '284': 'Protestant Denominations',
  '285': 'Presbyterian & Reformed Churches',
  '286': 'Baptist & Restoration Movement Churches',
  '287': 'Methodist & Related Churches',
  '289': 'Other Christian Denominations',
  '291': 'Comparative Religion',
  '292': 'Classical & Greek Religion',
  '293': 'Germanic Religion',
  '294': 'Religions of Indic Origin',
  '295': 'Zoroastrianism',
  '296': 'Judaism',
  '297': 'Islam',
  '299': 'Other Religions',

  // 300s — Social Sciences
  '301': 'Sociology & Anthropology',
  '302': 'Social Interaction',
  '303': 'Social Processes',
  '304': 'Factors Affecting Social Behavior',
  '305': 'Social Groups',
  '306': 'Culture & Institutions',
  '307': 'Communities',
  '308': 'Sociology of Specific Groups',
  '309': 'Sociology of Specific Places',
  '311': 'Collections & Analysis of Statistics',
  '312': 'Vital Statistics',
  '314': 'General Statistics of Europe',
  '315': 'General Statistics of Asia',
  '316': 'General Statistics of Africa',
  '317': 'General Statistics of North America',
  '318': 'General Statistics of South America',
  '319': 'General Statistics of Other Areas',
  '321': 'Systems of Governments',
  '322': 'Relation of State to Social Groups',
  '323': 'Civil & Political Rights',
  '324': 'The Political Process',
  '325': 'International Migration',
  '326': 'Slavery & Emancipation',
  '327': 'International Relations',
  '328': 'The Legislative Process',
  '329': 'Practical Politics',
  '331': 'Labor Economics',
  '332': 'Financial Economics',
  '333': 'Land & Energy Economics',
  '334': 'Cooperatives',
  '335': 'Socialism & Related Systems',
  '336': 'Public Finance',
  '337': 'International Economics',
  '338': 'Production Economics',
  '339': 'Macroeconomics',
  '341': 'Law of Nations (International Law)',
  '342': 'Constitutional & Administrative Law',
  '343': 'Military, Defense & Public Property Law',
  '344': 'Labor, Social Service & Education Law',
  '345': 'Criminal Law',
  '346': 'Private Law',
  '347': 'Civil Procedure & Courts',
  '348': 'Laws, Regulations & Cases',
  '349': 'Law of Specific Jurisdictions',
  '351': 'Public Administration',
  '352': 'General Considerations',
  '353': 'U.S. Federal & State Administration',
  '354': 'Administration of Economy & Environment',
  '355': 'Military Science',
  '356': 'Infantry Forces',
  '357': 'Mounted Forces',
  '358': 'Air & Other Forces',
  '359': 'Naval Forces',
  '361': 'Social Problems & Social Welfare',
  '362': 'Social Welfare Problems & Services',
  '363': 'Other Social Problems & Services',
  '364': 'Criminology',
  '365': 'Penal Institutions',
  '366': 'Associations',
  '367': 'General Clubs',
  '368': 'Insurance',
  '369': 'Miscellaneous Associations',
  '371': 'Schools & Their Activities',
  '372': 'Elementary Education',
  '373': 'Secondary Education',
  '374': 'Adult Education',
  '375': 'Curricula',
  '376': 'Education of Women',
  '377': 'Schools & Religion',
  '378': 'Higher Education',
  '379': 'Education & State',
  '381': 'Internal Commerce & Retail Trade',
  '382': 'International & Foreign Trade',
  '383': 'Postal Communication',
  '384': 'Communications & Telecommunications',
  '385': 'Railroads',
  '386': 'Inland Waterway Transportation',
  '387': 'Water, Air & Space Transportation',
  '388': 'Transportation by Road',
  '389': 'Metrology & Standardization',
  '391': 'Costume & Personal Appearance',
  '392': 'Customs of Life Cycle & Domestic Life',
  '393': 'Death Customs',
  '394': 'General Customs',
  '395': 'Etiquette',
  '396': 'Customs Relating to Women',
  '398': 'Folklore',
  '399': 'Customs of War & Diplomacy',

  // 400s — Language
  '401': 'Philosophy & Theory of Language',
  '402': 'Miscellany of Language',
  '403': 'Dictionaries of Language',
  '404': 'Special Topics in Language',
  '405': 'Serial Publications on Language',
  '406': 'Organizations of Language',
  '407': 'Education, Research in Language',
  '408': 'Language by Groups of People',
  '409': 'Geography & History of Language',
  '411': 'Writing Systems & Phonology',
  '412': 'Etymology',
  '413': 'Dictionaries',
  '414': 'Phonology & Phonetics',
  '415': 'Structural Systems (Grammar)',
  '417': 'Dialectology & Historical Linguistics',
  '418': 'Applied Linguistics & Language Teaching',
  '419': 'Sign Languages',
  '421': 'Written & Spoken English',
  '422': 'Etymology of English',
  '423': 'English Dictionaries',
  '425': 'English Grammar',
  '427': 'English Dialects & Usage',
  '428': 'Standard English Usage',
  '429': 'Old English (Anglo-Saxon)',
  '431': 'Written & Spoken German',
  '433': 'German Dictionaries',
  '435': 'German Grammar',
  '438': 'Standard German Usage',
  '439': 'Other Germanic Languages',
  '441': 'Written & Spoken French',
  '443': 'French Dictionaries',
  '445': 'French Grammar',
  '448': 'Standard French Usage',
  '449': 'Occitan & Catalan Languages',
  '451': 'Written & Spoken Italian',
  '453': 'Italian Dictionaries',
  '455': 'Italian Grammar',
  '458': 'Standard Italian Usage',
  '461': 'Written & Spoken Spanish',
  '463': 'Spanish Dictionaries',
  '465': 'Spanish Grammar',
  '468': 'Standard Spanish Usage',
  '469': 'Portuguese Language',
  '471': 'Written & Spoken Classical Latin',
  '473': 'Classical Latin Dictionaries',
  '475': 'Classical Latin Grammar',
  '478': 'Classical Latin Usage',
  '479': 'Other Italic Languages',
  '481': 'Written & Spoken Classical Greek',
  '483': 'Classical Greek Dictionaries',
  '485': 'Classical Greek Grammar',
  '488': 'Classical Greek Usage',
  '489': 'Other Hellenic Languages',
  '491': 'East Indo-European & Celtic Languages',
  '492': 'Afro-Asiatic Languages',
  '493': 'Non-Semitic Afro-Asiatic Languages',
  '494': 'Altaic, Uralic & Hyperborean Languages',
  '495': 'Languages of East & Southeast Asia',
  '496': 'African Languages',
  '497': 'North American Native Languages',
  '498': 'South American Native Languages',
  '499': 'Miscellaneous Languages',

  // 500s — Science
  '501': 'Philosophy & Theory of Science',
  '502': 'Miscellany of Science',
  '503': 'Dictionaries of Science',
  '505': 'Serial Publications on Science',
  '506': 'Organizations of Science',
  '507': 'Education & Research in Science',
  '508': 'Natural History',
  '509': 'History & Geography of Science',
  '511': 'General Principles of Mathematics',
  '512': 'Algebra',
  '513': 'Arithmetic',
  '514': 'Topology',
  '515': 'Analysis & Calculus',
  '516': 'Geometry',
  '517': 'Mathematical Analysis',
  '518': 'Numerical Analysis',
  '519': 'Probability, Statistics & Applied Mathematics',
  '521': 'Celestial Mechanics',
  '522': 'Astronomical Instruments & Techniques',
  '523': 'Specific Celestial Bodies & Phenomena',
  '524': 'Unassigned',
  '525': 'Earth & Related Objects',
  '526': 'Mathematical Geography',
  '527': 'Celestial Navigation',
  '528': 'Ephemerides',
  '529': 'Chronology',
  '531': 'Classical Mechanics',
  '532': 'Fluid Mechanics',
  '533': 'Pneumatics (Gas Mechanics)',
  '534': 'Sound & Related Vibrations',
  '535': 'Light & Infrared & Ultraviolet Radiation',
  '536': 'Heat',
  '537': 'Electricity & Electronics',
  '538': 'Magnetism',
  '539': 'Modern Physics',
  '541': 'Physical Chemistry',
  '542': 'Chemical Laboratory Techniques',
  '543': 'Analytical Chemistry',
  '544': 'Qualitative Analysis',
  '545': 'Quantitative Analysis',
  '546': 'Inorganic Chemistry',
  '547': 'Organic Chemistry',
  '548': 'Crystallography',
  '549': 'Mineralogy',
  '551': 'Geology, Hydrology & Meteorology',
  '552': 'Petrology',
  '553': 'Economic Geology',
  '554': 'Geology of Europe',
  '555': 'Geology of Asia',
  '556': 'Geology of Africa',
  '557': 'Geology of North America',
  '558': 'Geology of South America',
  '559': 'Geology of Other Areas',
  '561': 'Paleobotany',
  '562': 'Fossil Invertebrates',
  '563': 'Fossil Protozoa & Lower Animals',
  '564': 'Fossil Mollusca & Related Phyla',
  '565': 'Fossil Arthropoda',
  '566': 'Fossil Vertebrata',
  '567': 'Fossil Cold-Blooded Vertebrates',
  '568': 'Fossil Aves (Birds)',
  '569': 'Fossil Mammalia',
  '571': 'Physiology & Related Subjects',
  '572': 'Biochemistry',
  '573': 'Specific Physiological Processes',
  '574': 'Unassigned',
  '575': 'Specific Parts of Plants',
  '576': 'Genetics & Evolution',
  '577': 'Ecology',
  '578': 'Natural History of Organisms',
  '579': 'Microorganisms, Fungi & Algae',
  '581': 'Specific Topics in Natural History of Plants',
  '582': 'Plants with Specific Characteristics',
  '583': 'Dicotyledons',
  '584': 'Monocotyledons',
  '585': 'Gymnosperms',
  '586': 'Seedless Plants',
  '587': 'Vascular Seedless Plants',
  '588': 'Bryophyta',
  '589': 'Unassigned',
  '591': 'Specific Topics in Natural History of Animals',
  '592': 'Invertebrates',
  '593': 'Marine & Seashore Invertebrates',
  '594': 'Mollusca & Molluscoidea',
  '595': 'Arthropoda',
  '596': 'Chordata',
  '597': 'Cold-Blooded Vertebrates',
  '598': 'Birds',
  '599': 'Mammalia (Mammals)',

  // 600s — Technology
  '601': 'Philosophy & Theory of Technology',
  '602': 'Miscellany of Technology',
  '603': 'Dictionaries of Technology',
  '604': 'Technical Drawing, Graphic Methods',
  '605': 'Serial Publications of Technology',
  '606': 'Organizations of Technology',
  '607': 'Education, Research in Technology',
  '608': 'Inventions & Patents',
  '609': 'History of Technology',
  '611': 'Human Anatomy',
  '612': 'Human Physiology',
  '613': 'Hygiene & Personal Health',
  '614': 'Public Health & Preventive Medicine',
  '615': 'Pharmacology & Therapeutics',
  '616': 'Diseases',
  '617': 'Surgery',
  '618': 'Gynecology & Pediatrics',
  '619': 'Veterinary Medicine',
  '621': 'Applied Physics & Mechanical Engineering',
  '622': 'Mining Engineering',
  '623': 'Military Engineering',
  '624': 'Civil Engineering',
  '625': 'Railway Engineering',
  '626': 'Unassigned',
  '627': 'Hydraulic Engineering',
  '628': 'Environmental Engineering',
  '629': 'Other Engineering',
  '631': 'Techniques & Equipment in Agriculture',
  '632': 'Plant Injuries, Diseases & Pests',
  '633': 'Field & Plantation Crops',
  '634': 'Orchards, Fruits & Forestry',
  '635': 'Horticulture & Garden Crops',
  '636': 'Animal Husbandry',
  '637': 'Processing Dairy Products',
  '638': 'Insect Culture',
  '639': 'Hunting, Fishing & Conservation',
  '641': 'Food & Drink',
  '642': 'Meals & Table Service',
  '643': 'Housing & Household Equipment',
  '644': 'Household Utilities',
  '645': 'Household Furnishings',
  '646': 'Sewing, Clothing & Management',
  '647': 'Management of Public Households',
  '648': 'Housekeeping',
  '649': 'Child Rearing & Home Care',
  '651': 'Office Services',
  '652': 'Processes of Written Communication',
  '653': 'Shorthand',
  '654': 'Unassigned',
  '655': 'Unassigned',
  '656': 'Unassigned',
  '657': 'Accounting',
  '658': 'Management',
  '659': 'Advertising & Public Relations',
  '661': 'Industrial Chemicals',
  '662': 'Explosives, Fuels & Related Products',
  '663': 'Beverage Technology',
  '664': 'Food Technology',
  '665': 'Industrial Oils, Fats, Waxes & Gases',
  '666': 'Ceramic & Allied Technologies',
  '667': 'Cleaning, Color & Related Technologies',
  '668': 'Technology of Other Organic Products',
  '669': 'Metallurgy',
  '671': 'Metalworking & Primary Metal Products',
  '672': 'Iron, Steel & Other Iron Alloys',
  '673': 'Nonferrous Metals',
  '674': 'Lumber Processing & Wood Products',
  '675': 'Leather & Fur Technology',
  '676': 'Pulp & Paper Technology',
  '677': 'Textiles',
  '678': 'Elastomers & Elastomer Products',
  '679': 'Other Products of Specific Materials',
  '681': 'Precision Instruments & Optical Engineering',
  '682': 'Small Forge Work (Blacksmithing)',
  '683': 'Hardware & Household Appliances',
  '684': 'Furnishings & Home Workshops',
  '685': 'Leather, Fur & Related Products',
  '686': 'Printing & Related Activities',
  '687': 'Clothing & Accessories',
  '688': 'Other Manufacturing',
  '689': 'Unassigned',
  '691': 'Building Materials',
  '692': 'Auxiliary Construction Practices',
  '693': 'Specific Materials in Construction',
  '694': 'Wood Construction (Carpentry)',
  '695': 'Roof Covering',
  '696': 'Utilities in Buildings',
  '697': 'Heating, Ventilating & Air Conditioning',
  '698': 'Detail Finishing',
  '699': 'Unassigned',

  // 700s — Arts
  '701': 'Philosophy & Theory of Fine Arts',
  '702': 'Miscellany of Fine Arts',
  '703': 'Dictionaries of Fine Arts',
  '704': 'Special Topics in Fine Arts',
  '705': 'Serial Publications on Fine Arts',
  '706': 'Organizations of Fine Arts',
  '707': 'Education, Research in Fine Arts',
  '708': 'Art Galleries & Museums',
  '709': 'History & Biography in Arts',
  '711': 'Area Planning',
  '712': 'Landscape Architecture',
  '713': 'Landscape Architecture of Traffic Ways',
  '714': 'Unassigned',
  '715': 'Woody Plants in Landscape Architecture',
  '716': 'Herbaceous Plants in Landscape Architecture',
  '717': 'Structures in Landscape Architecture',
  '718': 'Landscape Design of Cemeteries',
  '719': 'Natural Landscapes',
  '721': 'Structural Elements of Architecture',
  '722': 'Ancient Architecture',
  '723': 'Medieval Architecture',
  '724': 'Modern Architecture',
  '725': 'Public Structures',
  '726': 'Buildings for Religious Purposes',
  '727': 'Buildings for Educational Purposes',
  '728': 'Residential Buildings',
  '729': 'Design & Decoration of Structures',
  '731': 'Processes, Forms & Subjects of Sculpture',
  '732': 'Primitive, Ancient & Oriental Sculpture',
  '733': 'Greek, Etruscan & Roman Sculpture',
  '734': 'Medieval Sculpture',
  '735': 'Modern Sculpture',
  '736': 'Carving & Carvings',
  '737': 'Numismatics & Sigillography',
  '738': 'Ceramic Arts',
  '739': 'Art Metalwork',
  '741': 'Drawing & Drawings',
  '742': 'Perspective in Drawing',
  '743': 'Drawing by Subject',
  '744': 'Unassigned',
  '745': 'Decorative Arts',
  '746': 'Textile Arts',
  '747': 'Interior Decoration',
  '748': 'Glass',
  '749': 'Furniture & Accessories',
  '751': 'Techniques, Equipment & Forms',
  '752': 'Color in Painting',
  '753': 'Symbolism, Allegory & Mythological Painting',
  '754': 'Genre Paintings',
  '755': 'Religious Painting',
  '757': 'Portrait Painting',
  '758': 'Other Subjects of Painting',
  '759': 'History, Biography & Geography of Painting',
  '761': 'Relief Processes',
  '763': 'Lithographic Processes',
  '764': 'Chromolithography & Serigraphy',
  '765': 'Metal Engraving',
  '766': 'Mezzotinting, Aquatinting & Related',
  '767': 'Etching & Drypoint',
  '769': 'Prints',
  '771': 'Techniques, Equipment & Materials',
  '772': 'Metallic Salt Processes',
  '773': 'Pigment Processes',
  '774': 'Holography',
  '775': 'Digital Photography',
  '776': 'Unassigned',
  '778': 'Fields & Kinds of Photography',
  '779': 'Photographs',
  '781': 'General Principles & Musical Forms',
  '782': 'Vocal Music',
  '783': 'Music for Single Voices',
  '784': 'Instruments & Instrumental Ensembles',
  '785': 'Ensembles with One Instrument per Part',
  '786': 'Keyboard & Other Instruments',
  '787': 'Stringed Instruments',
  '788': 'Wind Instruments',
  '789': 'Unassigned',
  '791': 'Public Performances',
  '792': 'Stage Presentations',
  '793': 'Indoor Games & Amusements',
  '794': 'Indoor Games of Skill',
  '795': 'Games of Chance',
  '796': 'Athletic & Outdoor Sports',
  '797': 'Aquatic & Air Sports',
  '798': 'Equestrian Sports',
  '799': 'Fishing, Hunting & Shooting',

  // 800s — Literature
  '801': 'Philosophy & Theory of Literature',
  '802': 'Miscellany of Literature',
  '803': 'Dictionaries of Literature',
  '805': 'Serial Publications on Literature',
  '806': 'Organizations in Literature',
  '807': 'Education, Research in Literature',
  '808': 'Rhetoric & Collections',
  '809': 'Literary History',
  '811': 'American Poetry in English',
  '812': 'American Drama in English',
  '813': 'American Fiction in English',
  '814': 'American Essays in English',
  '815': 'American Speeches in English',
  '816': 'American Letters in English',
  '817': 'American Humor & Satire',
  '818': 'American Miscellaneous Writings',
  '819': 'Canadian Literature in English',
  '821': 'English Poetry',
  '822': 'English Drama',
  '823': 'English Fiction',
  '824': 'English Essays',
  '825': 'English Speeches',
  '826': 'English Letters',
  '827': 'English Humor & Satire',
  '828': 'English Miscellaneous Writings',
  '829': 'Old English Literature',
  '831': 'German Poetry',
  '832': 'German Drama',
  '833': 'German Fiction',
  '834': 'German Essays',
  '836': 'German Letters',
  '837': 'German Humor & Satire',
  '838': 'German Miscellaneous Writings',
  '839': 'Other Germanic Literatures',
  '841': 'French Poetry',
  '842': 'French Drama',
  '843': 'French Fiction',
  '844': 'French Essays',
  '845': 'French Speeches',
  '846': 'French Letters',
  '847': 'French Humor & Satire',
  '848': 'French Miscellaneous Writings',
  '849': 'Occitan & Catalan Literatures',
  '851': 'Italian Poetry',
  '852': 'Italian Drama',
  '853': 'Italian Fiction',
  '854': 'Italian Essays',
  '855': 'Italian Speeches',
  '856': 'Italian Letters',
  '857': 'Italian Humor & Satire',
  '858': 'Italian Miscellaneous Writings',
  '859': 'Romanian & Related Literatures',
  '861': 'Spanish Poetry',
  '862': 'Spanish Drama',
  '863': 'Spanish Fiction',
  '864': 'Spanish Essays',
  '865': 'Spanish Speeches',
  '866': 'Spanish Letters',
  '867': 'Spanish Humor & Satire',
  '868': 'Spanish Miscellaneous Writings',
  '869': 'Portuguese Literature',
  '871': 'Classical Latin Poetry',
  '872': 'Classical Latin Drama',
  '873': 'Classical Latin Epic Poetry & Fiction',
  '874': 'Classical Latin Lyric Poetry',
  '875': 'Classical Latin Speeches',
  '876': 'Classical Latin Letters',
  '877': 'Classical Latin Humor & Satire',
  '878': 'Classical Latin Miscellaneous Writings',
  '879': 'Other Italic Literatures',
  '881': 'Classical Greek Poetry',
  '882': 'Classical Greek Drama',
  '883': 'Classical Greek Epic Poetry & Fiction',
  '884': 'Classical Greek Lyric Poetry',
  '885': 'Classical Greek Speeches',
  '886': 'Classical Greek Letters',
  '887': 'Classical Greek Humor & Satire',
  '888': 'Classical Greek Miscellaneous Writings',
  '889': 'Modern Greek Literature',
  '891': 'East Indo-European & Celtic Literatures',
  '892': 'Afro-Asiatic Literatures',
  '893': 'Non-Semitic Afro-Asiatic Literatures',
  '894': 'Altaic, Uralic & Hyperborean Literatures',
  '895': 'Literatures of East & Southeast Asia',
  '896': 'African Literatures',
  '897': 'North American Native Literatures',
  '898': 'South American Native Literatures',
  '899': 'Austronesian & Other Literatures',

  // 900s — History & Geography
  '901': 'Philosophy & Theory of History',
  '902': 'Miscellany of History',
  '903': 'Dictionaries of History',
  '904': 'Collected Accounts of Events',
  '905': 'Serial Publications of History',
  '906': 'Organizations of History',
  '907': 'Education, Research in History',
  '908': 'History by Groups of People',
  '909': 'World History',
  '911': 'Historical Geography',
  '912': 'Atlases & Maps',
  '913': 'Geography of Ancient World',
  '914': 'Geography of Europe',
  '915': 'Geography of Asia',
  '916': 'Geography of Africa',
  '917': 'Geography of North America',
  '918': 'Geography of South America',
  '919': 'Geography of Other Areas',
  '929': 'Genealogy & Heraldry',
  '931': 'History of China',
  '932': 'History of Egypt',
  '933': 'History of Palestine',
  '934': 'History of South Asia',
  '935': 'History of Mesopotamia',
  '936': 'History of Northern & Western Europe',
  '937': 'History of Italian Peninsula',
  '938': 'History of Greece',
  '939': 'History of Other Areas of Ancient World',
  '941': 'History of British Isles',
  '942': 'History of England',
  '943': 'History of Central Europe',
  '944': 'History of France',
  '945': 'History of Italian Peninsula',
  '946': 'History of Iberian Peninsula',
  '947': 'History of Eastern Europe',
  '948': 'History of Scandinavia',
  '949': 'History of Other Parts of Europe',
  '951': 'History of China',
  '952': 'History of Japan',
  '953': 'History of Arabian Peninsula',
  '954': 'History of South Asia & India',
  '955': 'History of Iran',
  '956': 'History of Middle East',
  '957': 'History of Siberia',
  '958': 'History of Central Asia',
  '959': 'History of Southeast Asia',
  '961': 'History of North Africa',
  '962': 'History of Egypt & Sudan',
  '963': 'History of Ethiopia',
  '964': 'History of Northwest Africa',
  '965': 'History of Algeria',
  '966': 'History of West Africa',
  '967': 'History of Central Africa',
  '968': 'History of Southern Africa',
  '969': 'History of South Indian Ocean Islands',
  '971': 'History of Canada',
  '972': 'History of Middle America',
  '973': 'History of United States',
  '974': 'History of Northeastern United States',
  '975': 'History of Southeastern United States',
  '976': 'History of South Central United States',
  '977': 'History of North Central United States',
  '978': 'History of Western United States',
  '979': 'History of Great Basin & Pacific Slope',
  '981': 'History of Brazil',
  '982': 'History of Argentina',
  '983': 'History of Chile',
  '984': 'History of Bolivia',
  '985': 'History of Peru',
  '986': 'History of Colombia & Ecuador',
  '987': 'History of Venezuela',
  '988': 'History of Guiana',
  '989': 'History of Paraguay & Uruguay',
  '991': 'Unassigned',
  '992': 'Unassigned',
  '993': 'History of New Zealand',
  '994': 'History of Australia',
  '995': 'History of Melanesia',
  '996': 'History of Polynesia & Micronesia',
  '997': 'History of Atlantic Ocean Islands',
  '998': 'History of Arctic & Antarctic',
  '999': 'Extraterrestrial Worlds',

  // ═══════════════════════════════════════════════════════════
  // LEVEL 4–6 — KEY SUB-SECTIONS FOR ACADEMIC LIBRARIES
  // ═══════════════════════════════════════════════════════════

  // ── Computer Science (004–006) ──────────────────────────────
  '004.0': 'Computer Science Fundamentals',
  '004.1': 'General Computing Concepts',
  '004.16': 'Personal Computers',
  '004.165': 'Laptop & Mobile Computers',
  '004.2': 'Computer Architecture',
  '004.21': 'Computer Design',
  '004.22': 'Pipeline Architecture',
  '004.25': 'Parallel Processing',
  '004.3': 'Storage & Input/Output',
  '004.35': 'Memory Technology',
  '004.56': 'Networking Fundamentals',
  '004.6': 'Interfacing & Communications',
  '004.62': 'Computer Peripherals',
  '004.64': 'Computer Networks',
  '004.65': 'Wide Area Networks',
  '004.67': 'The Internet',
  '004.678': 'World Wide Web',
  '004.68': 'Network Security',
  '005.1': 'Programming',
  '005.10': 'Programming Fundamentals',
  '005.11': 'Structured Programming',
  '005.12': 'Software Engineering & Lifecycle',
  '005.13': 'Programming Languages',
  '005.133': 'Specific Programming Languages',
  '005.14': 'Language Translation & Parsing',
  '005.15': 'Debugging & Testing',
  '005.2': 'Programming for Specific Platforms',
  '005.3': 'Software Programs & Applications',
  '005.36': 'Application Development',
  '005.4': 'Systems Programming',
  '005.42': 'Systems Software Design',
  '005.43': 'Operating Systems',
  '005.432': 'Unix & Linux',
  '005.437': 'Windows Operating Systems',
  '005.44': 'Operating Environments & Shells',
  '005.45': 'Memory Management',
  '005.5': 'General Purpose Application Software',
  '005.52': 'Word Processing',
  '005.53': 'Spreadsheets',
  '005.54': 'Databases & Records',
  '005.55': 'Presentation Software',
  '005.6': 'Microcomputer Programming',
  '005.7': 'Data & Database Management',
  '005.71': 'Database Theory',
  '005.72': 'Network Databases',
  '005.74': 'Relational Database Management',
  '005.745': 'NoSQL Databases',
  '005.75': 'Database Design',
  '005.756': 'Relational Databases & SQL',
  '005.758': 'Database Administration',
  '005.8': 'Data Security & Computer Security',
  '005.82': 'Cryptography & Encryption',
  '005.84': 'Malware & Virus Protection',
  '005.87': 'Access Control & Authentication',
  '006.2': 'Human-Computer Interaction',
  '006.3': 'Artificial Intelligence',
  '006.31': 'Machine Learning',
  '006.312': 'Deep Learning',
  '006.315': 'Reinforcement Learning',
  '006.32': 'Neural Networks & Connectionism',
  '006.33': 'Expert Systems & Knowledge Engineering',
  '006.35': 'Natural Language Processing',
  '006.37': 'Computer Vision & Image Recognition',
  '006.4': 'Pattern Recognition',
  '006.42': 'Biometric Systems',
  '006.6': 'Computer Graphics',
  '006.62': 'Two-Dimensional Graphics',
  '006.63': 'Three-Dimensional Graphics',
  '006.66': 'Animation',
  '006.68': 'Graphic Design Software',
  '006.7': 'Multimedia Computing',
  '006.74': 'Web Technologies',
  '006.76': 'Mobile Application Development',
  '006.8': 'Virtual Reality & Augmented Reality',

  // ── Mathematics (510–519) ────────────────────────────────────
  '510.2': 'Applied Mathematics',
  '510.7': 'Mathematics Education',
  '510.9': 'History of Mathematics',
  '511.3': 'Mathematical Logic & Foundations',
  '511.32': 'Set Theory',
  '511.33': 'Model Theory',
  '511.35': 'Computability & Recursion Theory',
  '511.36': 'Proof Theory',
  '511.4': 'Approximations & Expansions',
  '511.6': 'Combinatorics & Graph Theory',
  '511.64': 'Graph Theory',
  '511.8': 'Mathematical Modelling',
  '512.2': 'Group Theory',
  '512.22': 'Finite Groups',
  '512.3': 'Field Theory & Polynomials',
  '512.4': 'Ring Theory',
  '512.44': 'Commutative Rings',
  '512.46': 'Homological Algebra',
  '512.5': 'Linear & Multilinear Algebra',
  '512.52': 'Matrices',
  '512.55': 'Vector Spaces',
  '512.6': 'Category Theory',
  '512.7': 'Number Theory',
  '512.72': 'Analytic Number Theory',
  '512.74': 'Algebraic Number Theory',
  '512.76': 'Diophantine Equations',
  '512.8': 'Algebraic Geometry',
  '512.9': 'General Algebra',
  '514.2': 'Algebraic Topology',
  '514.22': 'Homotopy Theory',
  '514.23': 'Homology Theory',
  '514.3': 'General Topology',
  '514.32': 'Point-Set Topology',
  '515.2': 'Sequences, Series & Summability',
  '515.3': 'Differential Equations',
  '515.35': 'Ordinary Differential Equations',
  '515.353': 'Partial Differential Equations',
  '515.36': 'Dynamical Systems',
  '515.4': 'Measure Theory & Integration',
  '515.42': 'Lebesgue Integration',
  '515.43': 'Measure Theory',
  '515.5': 'Special Functions',
  '515.6': 'Calculus of Variations',
  '515.7': 'Functional Analysis',
  '515.72': 'Hilbert Spaces',
  '515.73': 'Banach Spaces',
  '515.8': 'Real Functions & Analysis',
  '515.9': 'Complex Analysis',
  '515.92': 'Analytic Functions',
  '515.94': 'Riemann Surfaces',
  '516.1': 'Combinatorial Geometry',
  '516.2': 'Euclidean Geometry',
  '516.3': 'Analytic & Projective Geometry',
  '516.35': 'Algebraic Curves',
  '516.36': 'Differential Geometry',
  '516.37': 'Riemannian Geometry',
  '518.1': 'Algorithms',
  '518.4': 'Numerical Linear Algebra',
  '518.5': 'Numerical Integration',
  '519.2': 'Probability & Stochastic Processes',
  '519.22': 'Probability Theory',
  '519.23': 'Random Processes',
  '519.24': 'Markov Chains',
  '519.3': 'Game Theory & Operations Research',
  '519.32': 'Game Theory',
  '519.4': 'Numerical Analysis',
  '519.5': 'Statistical Mathematics',
  '519.52': 'Bayesian Statistics',
  '519.53': 'Descriptive Statistics',
  '519.535': 'Regression Analysis',
  '519.54': 'Statistical Inference & Estimation',
  '519.55': 'Time Series Analysis',
  '519.56': 'Multivariate Analysis',
  '519.6': 'Mathematical Optimization',
  '519.62': 'Linear Programming',
  '519.64': 'Nonlinear Programming',
  '519.7': 'Mathematical Programming',
  '519.72': 'Integer Programming',
  '519.77': 'Network Analysis',

  // ── Physics (530–539) ────────────────────────────────────────
  '530.1': 'Theories of Physics',
  '530.11': 'Theory of Relativity',
  '530.12': 'Quantum Mechanics',
  '530.13': 'Statistical Mechanics',
  '530.14': 'Quantum Field Theory',
  '530.15': 'Mathematical Physics',
  '530.4': 'Condensed Matter & Solid State Physics',
  '530.41': 'Solid State Physics',
  '530.42': 'Liquid State Physics',
  '531.1': 'Mechanics of Particles',
  '531.3': 'Mechanics of Rigid Bodies',
  '531.38': 'Gyroscopes & Pendulums',
  '531.5': 'Gravity & Gravitation',
  '531.6': 'Statics',
  '532.0': 'Fluid Mechanics',
  '532.5': 'Viscous Flow',
  '532.6': 'Surface Tension',
  '533.2': 'Aerodynamics',
  '534.2': 'Acoustics & Sound Waves',
  '535.1': 'Optics & Nature of Light',
  '535.2': 'Reflection & Refraction',
  '535.3': 'Spectroscopy',
  '535.4': 'Polarization of Light',
  '535.8': 'Lasers & Masers',
  '536.2': 'Thermodynamics',
  '536.4': 'Heat Transfer',
  '537.1': 'Electromagnetic Theory',
  '537.2': 'Electrostatics',
  '537.3': 'Electric Current',
  '537.5': 'Electronics',
  '537.6': 'Magnetohydrodynamics',
  '538.3': 'Ferromagnetism',
  '538.4': 'Superconductivity',
  '539.1': 'Atomic & Nuclear Physics',
  '539.12': 'Nuclear Reactions',
  '539.2': 'Atomic Structure',
  '539.7': 'Nuclear & Subatomic Particles',
  '539.72': 'Particle Physics',
  '539.75': 'Radioactivity',

  // ── Chemistry (540–549) ──────────────────────────────────────
  '541.2': 'Atomic & Molecular Theory',
  '541.3': 'Physical Chemistry',
  '541.33': 'Chemical Thermodynamics',
  '541.36': 'Chemical Kinetics',
  '541.37': 'Electrochemistry',
  '541.38': 'Photochemistry',
  '542.1': 'Laboratory Equipment',
  '543.1': 'Spectrochemical Analysis',
  '543.5': 'Chromatographic Analysis',
  '546.2': 'Hydrogen & Related Elements',
  '546.3': 'Alkali Metals',
  '546.6': 'Transition Metals',
  '547.1': 'Physical Organic Chemistry',
  '547.2': 'Aliphatic Compounds',
  '547.4': 'Aromatic Compounds',
  '547.5': 'Heterocyclic Compounds',
  '547.6': 'Organometallic Compounds',
  '547.7': 'Polymers & Related Compounds',
  '547.8': 'Biochemistry',

  // ── Earth Sciences (550–559) ─────────────────────────────────
  '551.1': 'Gross Structure of Earth',
  '551.2': 'Volcanoes & Earthquakes',
  '551.21': 'Volcanoes',
  '551.22': 'Earthquakes & Seismology',
  '551.3': 'Weathering & Erosion',
  '551.46': 'Oceanography',
  '551.5': 'Meteorology',
  '551.51': 'Composition of Atmosphere',
  '551.55': 'Storms & Severe Weather',
  '551.56': 'Atmospheric Electricity & Optics',
  '551.57': 'Humidity & Precipitation',
  '551.6': 'Climatology',
  '551.68': 'Climate Change',

  // ── Biology (570–579) ────────────────────────────────────────
  '571.6': 'Cell Biology',
  '571.8': 'Development & Maturation',
  '572.4': 'Molecular Biology',
  '572.5': 'Proteins',
  '572.6': 'Enzymes',
  '572.8': 'Genetics & DNA',
  '572.86': 'Genomics',
  '572.87': 'Gene Expression',
  '576.5': 'Genetics',
  '576.54': 'Genetic Engineering',
  '576.58': 'Evolution',
  '576.8': 'Evolution',
  '577.1': 'Ecosystems',
  '577.3': 'Forest Ecology',
  '577.4': 'Grassland Ecology',
  '577.5': 'Freshwater Ecology',
  '577.7': 'Marine Ecology',

  // ── Medicine (610–619) ───────────────────────────────────────
  '610.7': 'Medical Education & Nursing',
  '610.72': 'Medical Research',
  '612.1': 'Cardiovascular System',
  '612.2': 'Respiratory System',
  '612.3': 'Digestive System',
  '612.4': 'Endocrine System',
  '612.6': 'Reproductive System',
  '612.8': 'Nervous System',
  '613.2': 'Nutrition',
  '613.7': 'Physical Fitness',
  '614.4': 'Public Health & Epidemiology',
  '615.1': 'Drugs & Related Substances',
  '615.3': 'Medications from Specific Sources',
  '615.5': 'Therapeutics',
  '616.1': 'Heart & Circulatory Diseases',
  '616.2': 'Respiratory Diseases',
  '616.3': 'Digestive Diseases',
  '616.4': 'Endocrine & Metabolic Diseases',
  '616.5': 'Skin Diseases',
  '616.6': 'Genitourinary Diseases',
  '616.7': 'Musculoskeletal Diseases',
  '616.8': 'Neurological Diseases',
  '616.9': 'Infectious & Contagious Diseases',
  '616.99': 'Cancer & Oncology',

  // ── Engineering (620–629) ────────────────────────────────────
  '620.0': 'Engineering Fundamentals',
  '620.00': 'General Engineering Principles',
  '620.1': 'Engineering Mechanics & Materials',
  '620.11': 'Materials Science & Properties',
  '620.112': 'Testing & Measurement of Materials',
  '620.113': 'Fatigue & Fracture of Materials',
  '620.116': 'Corrosion',
  '620.14': 'Vibration Engineering',
  '620.16': 'Heat Transfer',
  '620.17': 'Fluid Dynamics',
  '620.18': 'Dimensional Analysis',
  '620.2': 'Sound Engineering & Acoustics',
  '620.3': 'Mechanics of Deformable Bodies',
  '620.5': 'Nanotechnology',
  '620.8': 'Human Engineering & Ergonomics',
  '621.3': 'Electrical & Electronic Engineering',
  '621.31': 'Generation & Transmission of Electricity',
  '621.312': 'Power Generation',
  '621.313': 'Electric Motors & Generators',
  '621.314': 'Power Converters & Transformers',
  '621.319': 'Electrical Circuits',
  '621.32': 'Lighting Technology',
  '621.33': 'Electric Railroads',
  '621.34': 'Electrochemical Engineering',
  '621.35': 'Electrochemical Devices',
  '621.36': 'Optical Engineering & Lasers',
  '621.366': 'Lasers',
  '621.37': 'Electronic Instruments',
  '621.38': 'Electronics',
  '621.381': 'Semiconductor Electronics',
  '621.3810': 'Semiconductor Fundamentals',
  '621.3813': 'Amplifiers & Oscillators',
  '621.3815': 'Transistors & Semiconductor Devices',
  '621.38152': 'Bipolar Transistors (BJT)',
  '621.38153': 'Field-Effect Transistors (MOSFET)',
  '621.38154': 'Other Semiconductor Devices',
  '621.3817': 'Microwave Electronics',
  '621.3819': 'Digital Electronics & Logic Circuits',
  '621.382': 'Communications Engineering',
  '621.3821': 'Modulation & Signal Processing',
  '621.3822': 'Radio Frequency Engineering',
  '621.3824': 'Optical Communications & Fiber Optics',
  '621.383': 'Telephone & Telegraph Engineering',
  '621.384': 'Radio & Radar Engineering',
  '621.385': 'Television Engineering',
  '621.386': 'Satellite Communications',
  '621.387': 'Cable Television',
  '621.388': 'Underwater Acoustics',
  '621.39': 'Computer Engineering & Hardware',
  '621.391': 'Signal Processing',
  '621.395': 'Sound Recording & Reproduction',
  '621.396': 'Radio Communications',
  '621.4': 'Heat Engineering & Power Plants',
  '621.40': 'Heat Transfer Engineering',
  '621.41': 'Internal Combustion Engineering',
  '621.43': 'Internal Combustion Engines',
  '621.433': 'Diesel Engines',
  '621.434': 'Gas Turbines',
  '621.44': 'Rocket Propulsion',
  '621.45': 'Jet Propulsion',
  '621.46': 'Electric & Hybrid Propulsion',
  '621.48': 'Nuclear Engineering',
  '621.483': 'Nuclear Reactors',
  '621.5': 'Pneumatic, Hydraulic & Vacuum',
  '621.6': 'Blowers, Fans & Pumps',
  '621.7': 'Manufacturing & Production Engineering',
  '621.78': 'Heat Treatment',
  '621.8': 'Machine Engineering & Mechanisms',
  '621.82': 'Mechanical Drives & Gears',
  '621.83': 'Gears & Gear Trains',
  '621.84': 'Bearings',
  '621.86': 'Conveying Equipment',
  '621.9': 'Tools & Cutting Technology',
  '621.97': 'Welding Engineering',
  '622.1': 'Mining Surveying & Prospecting',
  '623.4': 'Ordnance & Firearms',
  '623.7': 'Military Vehicles & Aircraft',
  '624.1': 'Structural Engineering',
  '624.15': 'Foundation Engineering',
  '624.17': 'Structural Analysis',
  '624.171': 'Finite Element Analysis',
  '624.18': 'Structural Materials',
  '624.182': 'Steel Structures',
  '624.183': 'Reinforced Concrete',
  '624.2': 'Bridge Engineering',
  '624.5': 'Tunnels & Tunnel Engineering',
  '627.1': 'Hydraulic Engineering',
  '627.2': 'Harbor & Port Engineering',
  '627.4': 'River Engineering & Flood Control',
  '627.5': 'Dams & Reservoirs',
  '627.8': 'Irrigation Engineering',
  '628.1': 'Water Supply Engineering',
  '628.2': 'Sewage Treatment',
  '628.3': 'Wastewater Treatment',
  '628.4': 'Waste Technology',
  '628.5': 'Pollution Control Technology',
  '628.53': 'Air Pollution Control',
  '628.55': 'Noise Pollution Control',
  '629.1': 'Aerospace Engineering',
  '629.12': 'Aerodynamics',
  '629.13': 'Aeronautical Engineering',
  '629.132': 'Aircraft Design',
  '629.135': 'Helicopters & Rotorcraft',
  '629.2': 'Automotive Engineering',
  '629.22': 'Passenger Automobiles',
  '629.23': 'Truck & Bus Engineering',
  '629.25': 'Tire Technology',
  '629.28': 'Automotive Testing',
  '629.4': 'Astronautics & Space Technology',
  '629.41': 'Rocket Engineering',
  '629.44': 'Spacecraft Design',
  '629.45': 'Manned Spaceflight',
  '629.46': 'Unmanned Spacecraft',
  '629.47': 'Space Navigation',
  '629.8': 'Automatic Control Engineering',
  '629.83': 'Control Systems Theory',
  '629.836': 'Feedback Control Systems',
  '629.84': 'Process Control',
  '629.89': 'Robotics',
  '629.892': 'Robot Manipulators',
  '629.895': 'Robot Sensors & Actuators',

  // ── Management & Business (650–659) ─────────────────────────
  '651.3': 'Records Management',
  '651.7': 'Communication in Business',
  '657.1': 'Principles of Accounting',
  '657.2': 'Financial Accounting',
  '657.3': 'Financial Statements',
  '657.4': 'Management Accounting',
  '657.45': 'Auditing',
  '657.46': 'Taxation Accounting',
  '657.48': 'Cost Accounting',
  '657.6': 'Accounting Systems & Methods',
  '658.0': 'Management Science',
  '658.00': 'Fundamentals of Management',
  '658.1': 'Organization & Financial Management',
  '658.11': 'Entrepreneurship',
  '658.15': 'Financial Management',
  '658.16': 'Profit & Loss Management',
  '658.2': 'Plant Management',
  '658.3': 'Personnel Management',
  '658.31': 'Human Resource Management',
  '658.314': 'Employee Motivation',
  '658.315': 'Employee Compensation',
  '658.32': 'Recruitment & Selection',
  '658.4': 'Executive Management & Strategy',
  '658.40': 'Decision Making',
  '658.401': 'Decision Analysis',
  '658.402': 'Management Information Systems',
  '658.403': 'Operations Research',
  '658.404': 'Project Management',
  '658.406': 'Risk Management',
  '658.41': 'General Management',
  '658.42': 'Leadership',
  '658.43': 'Organizational Theory',
  '658.44': 'Communication in Management',
  '658.45': 'Public Relations',
  '658.46': 'Corporate Governance',
  '658.47': 'Crisis Management',
  '658.5': 'Operations Management',
  '658.51': 'Production Planning',
  '658.514': 'Supply Chain Management',
  '658.52': 'Process Management',
  '658.56': 'Quality Management',
  '658.562': 'Total Quality Management',
  '658.57': 'Research & Development',
  '658.7': 'Procurement & Materials Management',
  '658.72': 'Purchasing',
  '658.78': 'Logistics & Distribution',
  '658.8': 'Marketing',
  '658.81': 'Sales Management',
  '658.82': 'Selling & Sales Techniques',
  '658.83': 'Market Research',
  '658.84': 'Advertising',
  '658.87': 'Consumer Behavior',
  '659.1': 'Advertising',
  '659.2': 'Public Relations',

  // ── Chemical Engineering (660–669) ──────────────────────────
  '660.2': 'Chemical Process Engineering',
  '660.28': 'Chemical Plant Design',
  '660.284': 'Distillation & Separation',
  '660.285': 'Reactors & Reaction Engineering',
  '660.28497': 'Thermodynamics in Chemical Engineering',
  '660.6': 'Biotechnology',
  '660.62': 'Genetic Engineering Applications',
  '660.63': 'Fermentation Technology',
  '661.0': 'Industrial Inorganic Chemistry',
  '661.8': 'Industrial Organic Chemistry',
  '664.0': 'Food Science & Technology',
  '669.1': 'Ferrous Metals & Iron Production',
  '669.7': 'Steel Production',
  '669.9': 'Powder Metallurgy',

  // ── Architecture & Design (720–749) ─────────────────────────
  '720.1': 'Philosophy & Theory of Architecture',
  '720.47': 'Sustainable Architecture',
  '721.0': 'Structural Components',
  '724.6': 'Architecture since 1945',
  '725.1': 'Government Buildings',
  '726.1': 'Temples & Religious Buildings',
  '728.0': 'Residential Architecture',
  '729.1': 'Architectural Decoration',
  '745.4': 'Industrial Design',
  '745.6': 'Typography & Lettering Design',
  '746.9': 'Textile Crafts',
  '747.0': 'Interior Design',

  // ── Education (370–379) ──────────────────────────────────────
  '370.1': 'Philosophy & Social Aspects of Education',
  '370.11': 'Education & Society',
  '370.15': 'Educational Psychology',
  '371.1': 'Teaching & Teaching Methods',
  '371.14': 'Teachers & Teaching',
  '371.2': 'School Administration',
  '371.3': 'Methods of Instruction',
  '371.33': 'Audio-Visual Education',
  '371.334': 'Computer-Based Education',
  '371.36': 'Project Learning',
  '371.39': 'Alternative Educational Methods',
  '371.4': 'Student Guidance & Counseling',
  '371.7': 'Student Health & Safety',
  '371.8': 'Students',
  '372.1': 'Elementary School Organization',
  '373.1': 'Secondary School Organization',
  '378.1': 'University & College Administration',
  '378.17': 'Higher Education Teaching Methods',

  // ── Law (340–349) ────────────────────────────────────────────
  '341.1': 'States & Individuals in International Law',
  '341.2': 'International Organizations',
  '341.4': 'International Crimes',
  '341.5': 'Conflict of Laws',
  '341.6': 'International Humanitarian Law',
  '341.7': 'Diplomacy',
  '342.0': 'Constitutional Law',
  '342.06': 'Constitutional Powers',
  '342.08': 'Civil Rights & Constitutional Rights',
  '343.0': 'Administrative Law',
  '344.0': 'Social Law',
  '345.0': 'Criminal Law',
  '346.0': 'Private Law',
  '346.04': 'Property Law',
  '346.07': 'Commercial Law',
  '346.082': 'Company & Corporation Law',
  '347.0': 'Civil Procedure',

  // ── Political Science (320–329) ──────────────────────────────
  '320.1': 'State & Government Theory',
  '320.5': 'Political Ideologies',
  '320.51': 'Liberalism',
  '320.53': 'Fascism',
  '320.54': 'Nationalism',
  '320.57': 'Libertarianism',
  '321.0': 'Systems of Government',
  '321.8': 'Democratic Government',
  '322.0': 'State & Church Relations',
  '323.0': 'Civil Rights',
  '323.4': 'Political Freedom',
  '324.0': 'Elections & Voting',
  '327.1': 'Foreign Policy',
  '327.17': 'International Security',

  // ── Economics (330–339) ──────────────────────────────────────
  '330.1': 'Economic Systems & Schools',
  '330.12': 'Capitalism',
  '330.15': 'Marxian Economics',
  '331.1': 'Labor Market',
  '331.2': 'Conditions of Employment',
  '331.7': 'Occupations & Industries',
  '332.1': 'Banks & Banking',
  '332.15': 'Central Banking',
  '332.4': 'Money & Monetary Policy',
  '332.6': 'Investments',
  '332.63': 'Stock Markets & Securities',
  '332.64': 'Futures & Derivatives',
  '332.7': 'Credit & Loans',
  '332.8': 'Interest & Discount',
  '333.7': 'Natural Resources',
  '333.72': 'Conservation',
  '333.79': 'Energy Economics',
  '333.91': 'Water Supply Economics',
  '334.0': 'Cooperative Organizations',
  '335.4': 'Marxian Economics',
  '336.2': 'Taxes & Taxation',
  '337.1': 'International Trade Theory',
  '338.1': 'Agricultural Economics',
  '338.4': 'Industrial Economics',
  '338.5': 'Prices',
  '338.6': 'Market Structure',
  '338.9': 'Economic Development',
  '339.2': 'National Income & Product Accounts',
  '339.3': 'Money Supply & Monetary Policy',
  '339.4': 'Aggregate Demand',
  '339.5': 'Inflation & Unemployment',

  // ── Sociology (300–309) ──────────────────────────────────────
  '302.2': 'Communication & Mass Media',
  '302.23': 'Mass Media',
  '302.231': 'Social Media',
  '302.3': 'Social Control',
  '302.5': 'Relationship of Individual to Society',
  '303.3': 'Political Processes',
  '303.4': 'Technological & Social Change',
  '304.2': 'Human Ecology',
  '304.6': 'Population',
  '304.66': 'Migration',
  '305.2': 'Young People',
  '305.3': 'Gender',
  '305.4': 'Women',
  '305.5': 'Social Classes',
  '305.56': 'Working Class',
  '305.8': 'Racial, Ethnic & National Groups',
  '306.2': 'Political Institutions',
  '306.3': 'Economic Institutions',
  '306.36': 'Slavery',
  '306.4': 'Cultural Institutions',
  '306.44': 'Language & Cultural Institutions',
  '306.7': 'Institutions Pertaining to Relationships',
  '306.8': 'Marriage & Family',

  // ── Psychology (150–159) ─────────────────────────────────────
  '152.1': 'Senses & Perception',
  '152.14': 'Visual Perception',
  '152.3': 'Conscious Mental Activities & Emotions',
  '152.4': 'Emotions & Feelings',
  '153.1': 'Memory',
  '153.2': 'Mental Imagery',
  '153.3': 'Imagination & Creativity',
  '153.4': 'Thought & Reasoning',
  '153.6': 'Communication Psychology',
  '153.7': 'Perceptual Processes',
  '153.8': 'Will & Motivation',
  '153.9': 'Intelligence & Aptitudes',
  '154.2': 'The Unconscious',
  '154.6': 'Hypnosis',
  '155.2': 'Individual Psychology & Personality',
  '155.3': 'Sex Psychology',
  '155.4': 'Child Psychology',
  '155.5': 'Adolescent & Adult Psychology',
  '155.6': 'Gender Psychology',
  '155.7': 'Evolutionary Psychology',
  '155.9': 'Environmental Psychology',
  '158.1': 'Personal Development & Self-Help',
  '158.2': 'Interpersonal Relations',
  '158.3': 'Group Psychology',
  '158.7': 'Occupational & Vocational Psychology',

  // ── 001 Knowledge & Learning ──────────────────────────────────
  '001.01': 'Theory of Knowledge',
  '001.1': 'Intellectual Life',
  '001.2': 'Scholarship & Learning',
  '001.3': 'Humanities',
  '001.4': 'Research',
  '001.42': 'Research Methods',
  '001.422': 'Statistical Research Methods',
  '001.424': 'Qualitative Research Methods',
  '001.43': 'Research Techniques',
  '001.44': 'Surveys & Opinion Polls',
  '001.5': 'Communication & Information Theory',
  '001.51': 'Communication Theory',
  '001.53': 'Semiotics',
  '001.54': 'Languages of Communication',
  '001.6': 'Data Processing & Information Systems',
  '001.64': 'Computer Processing',

  // ── 004 Computer Science ──────────────────────────────────────
  '004.01': 'Philosophy & Theory of Computing',
  '004.02': 'Miscellany of Computing',
  '004.11': 'Supercomputers & Mainframes',
  '004.12': 'Minicomputers',
  '004.167': 'Wearable Computers',
  '004.19': 'Other Computers',
  '004.5': 'Input/Output',
  '004.7': 'Computer Communications',
  '004.75': 'Distributed Systems',
  '004.8': 'Artificial Intelligence',

  // ── 005 Computer Programming ──────────────────────────────────
  '005.26': 'Programs for Specific Computers',
  '005.37': 'Database Management',
  '005.57': 'Desktop Publishing',
  '005.741': 'Relational Databases',
  '005.742': 'Object-Oriented Databases',

  // ── 006 Special Computer Methods ──────────────────────────────
  '006.5': 'Computer Sound Synthesis',
  '006.64': 'Computer Animation',

  // ── 100s Philosophy decimal subdivisions ─────────────────────
  '102': 'Miscellany of Philosophy',
  '105': 'Serial Publications in Philosophy',
  '121.2': 'Sources of Knowledge',
  '121.3': 'Validity & Truth',
  '121.4': 'Certainty & Doubt',
  '121.6': 'Memory as a Source of Knowledge',
  '121.68': 'Intuition',
  '128.2': 'Mind-Body Relationship',
  '128.3': 'Mind & Spirit',
  '128.37': 'Freedom of Will',
  '128.4': 'Meaning & Value of Human Life',
  '128.46': 'Death & Dying',
  '145': 'Sensationalism',
  '148': 'Liberalism, Eclecticism & Traditionalism',
  '167': 'Hypotheses',
  '169': 'Analogy',

  // ── 150s Psychology decimal subdivisions ─────────────────────
  '151': 'Physiological Psychology',
  '152.5': 'Motivation & Drives',
  '154.63': 'Sleep & Dreams',
  '155.8': 'Cross-Cultural Psychology',
  '157': 'Abnormal & Clinical Psychology',

  // ── 200s Religion decimal subdivisions ───────────────────────
  '216': 'Good & Evil',
  '217': 'Prayer',
  '248.4': 'Christian Life & Practice',
  '288': 'Unitarianism',
  '294.3': 'Buddhism',
  '294.32': 'Buddhist Sacred Books',
  '294.34': 'Buddhist Doctrines',
  '294.35': 'Buddhist Practice',
  '294.36': 'Buddhist Temples & Monastic Life',
  '294.38': 'Buddhist Sects & Movements',
  '294.4': 'Jainism',
  '294.5': 'Hinduism',
  '294.50': 'Hinduism — General & Biographical Works',
  '294.503': 'Hindu Encyclopedias & Dictionaries',
  '294.509': 'History of Hinduism',
  '294.5092': 'Hindu Saints & Teachers',
  '294.5095': 'Hinduism in Specific Regions',
  '294.50954': 'Hinduism in India',
  '294.51': 'Sacred Books of Hinduism',
  '294.513': 'Upanishads',
  '294.52': 'Doctrines of Hinduism',
  '294.54': 'Hindu Practices & Customs',
  '294.543': 'Hindu Festivals & Ceremonies',
  '294.5432': 'Hindu Pilgrimages',
  '294.544': 'Hindu Yoga & Meditation',
  '294.55': 'Hindu Worship',
  '294.56': 'Hindu Temples & Sacred Places',
  '294.57': 'Hindu Missions & Propagation',
  '294.58': 'Hindu Sects & Movements',
  '294.59': 'Smriti Literature (Puranas & Epics)',
  '294.592': 'Hindu Puranas',
  '294.5921': 'Ramayana',
  '294.59218': 'Ramayana Adaptations',
  '294.5922': 'Mahabharata',
  '294.5923': 'Bhagavata Purana (Bhagavatam)',
  '294.5924': 'Bhagavad Gita',
  '294.5925': 'Upanishads (Smriti)',
  '294.5926': 'Yoga Sutras of Patanjali',
  '294.593': 'Tantra',
  '294.594': 'Folk Hinduism',
  '294.6': 'Sikhism',
  '294.61': 'Sikh Sacred Texts — Guru Granth Sahib',
  '296.1': 'Torah & Talmud',
  '296.3': 'Jewish Theology',
  '296.4': 'Jewish Traditions & Practice',
  '297.1': 'Quran & Hadith',
  '297.2': 'Islamic Theology',
  '297.3': 'Islamic Practice & Worship',
  '297.4': 'Sufism & Mysticism',
  '297.5': 'Islamic Ethics',
  '297.6': 'Islamic Leaders',
  '297.7': 'Propagation of Islam',
  '297.9': 'Sects & Movements in Islam',
  '299.5': 'Religions of East & Southeast Asia',
  '299.51': 'Taoism',
  '299.512': 'Confucianism',

  // ── 300s Social Sciences decimal subdivisions ─────────────────
  '302.4': 'Social Role & Status',
  '303.32': 'Social Influence',
  '303.48': 'Causes of Social Change',
  '303.49': 'Forecasting Social Change',
  '303.6': 'Conflict',
  '304.8': 'Migration',
  '305.6': 'Religious Groups',
  '305.7': 'Language Groups',
  '306.42': 'Science & Society',
  '306.46': 'Technology & Culture',
  '306.48': 'Sports & Culture',
  '306.6': 'Religious Institutions',
  '306.85': 'Family',
  '306.9': 'Institutions of Death',
  '307.1': 'Planning & Development',
  '307.3': 'Community Structures',
  '307.7': 'Urban Communities',
  '307.72': 'Rural Communities',
  '320.3': 'Comparative Government',
  '320.52': 'Conservatism',
  '320.56': 'Green Politics & Ecology',
  '320.58': 'Communism',
  '321.4': 'Democracy',
  '321.5': 'Monarchies & Aristocracies',
  '321.6': 'Dictatorships',
  '321.7': 'Democratic Systems',
  '321.9': 'Authoritarian Government',
  '323.1': 'Civil Rights of Minorities',
  '323.2': 'Political Participation',
  '323.3': 'Civil Rights of Special Groups',
  '323.6': 'Citizenship',
  '324.2': 'Political Parties',
  '324.6': 'Election Systems & Voting',
  '324.9': 'Electoral History',
  '327.7': 'Foreign Relations of the United States',
  '330.156': 'Communism',
  '331.3': 'Special Categories of Workers',
  '331.4': 'Women in the Labor Force',
  '331.5': 'Special Categories of Workers',
  '331.8': 'Labor Unions & Related Organizations',
  '332.12': 'Commercial Banks',
  '332.2': 'Specialized Banking',
  '332.3': 'Credit & Loans',
  '332.41': 'Gold Standard',
  '332.45': 'Foreign Exchange',
  '332.46': 'Inflation & Deflation',
  '333.75': 'Forests',
  '333.8': 'Subsurface Resources',
  '333.9': 'Other Natural Resources',
  '336.3': 'Public Debt',
  '338.2': 'Mines & Mining',
  '338.3': 'Fishing, Hunting & Forestry',
  '338.7': 'Business Enterprises',
  '355.02': 'War & Warfare',
  '362.1': 'Physical Illness',
  '362.2': 'Mental Illness',
  '362.3': 'Mental Retardation',
  '362.4': 'Disabilities',
  '362.5': 'Poverty',
  '362.7': 'Problems of Young People',
  '362.8': 'Other Groups',
  '363.2': 'Police & Law Enforcement',
  '363.7': 'Environmental Problems',
  '363.8': 'Food & Population',
  '364.1': 'Offenses',
  '364.3': 'Offenders',
  '364.4': 'Prevention of Crime',
  '364.6': 'Penology',
  '370.9': 'Education by Area',
  '371.5': 'School Discipline',
  '371.6': 'Physical Plant',
  '371.9': 'Special Education',
  '378.12': 'Faculty & Teaching',
  '378.3': 'Student Aid',
  '378.4': 'Universities in Europe',
  '378.5': 'Universities in Asia',
  '378.7': 'Universities in North America',
  '384.3': 'Computer Communication',
  '384.5': 'Wireless Communication',
  '384.54': 'Radio Communication',
  '384.55': 'Television',
  '384.6': 'Telephone & Telegraph',
  '398.2': 'Folk Literature',

  // ── 400s Language decimal subdivisions ───────────────────────
  '401.4': 'Semantics',
  '401.41': 'Semiotics',
  '401.43': 'Metaphor',
  '401.9': 'Sociolinguistics',
  '415.5': 'Syntax',
  '418.4': 'Reading',
  '418.007': 'Language Teaching',
  '424': 'English Phonology',
  '428.1': 'English Vocabulary',
  '428.2': 'English Grammar for Specific Readers',
  '428.4': 'English Reading',
  '428.6': 'English for Children',
  '491.2': 'Sanskrit',
  '491.4': 'Indo-Aryan Languages',
  '491.43': 'Hindi',
  '491.44': 'Urdu',
  '491.48': 'Bengali',
  '491.5': 'Iranian Languages',
  '491.6': 'Celtic Languages',
  '491.7': 'Russian & East Slavic Languages',
  '491.8': 'Slavic Languages',
  '492.4': 'Hebrew',
  '492.7': 'Arabic',
  '494.3': 'Turkish Language',
  '495.1': 'Chinese Language',
  '495.6': 'Japanese Language',
  '495.7': 'Korean Language',

  // ── 500s Natural Sciences detailed subdivisions ───────────────
  '507.1': 'Science Education',
  '511.1': 'Mathematical Logic',
  '511.2': 'Finite Mathematics',
  '511.5': 'Graph Theory',
  '512.1': 'Algebraic Structures',
  '512.73': 'Algebraic Integers',
  '514.7': 'Differential Topology',
  '515.1': 'Topological Spaces',
  '515.23': 'Convergence & Divergence of Series',
  '515.75': 'Hilbert Spaces',
  '515.98': 'Fourier Analysis',
  '516.5': 'Configurations',
  '519.1': 'Graph Theory & Combinatorics',
  '519.76': 'Integer Programming',
  '523.1': 'The Universe & Galaxies',
  '523.2': 'Solar System',
  '523.3': 'Moon',
  '523.4': 'Planets',
  '523.5': 'Meteorites & Comets',
  '523.7': 'Sun',
  '523.8': 'Stars',
  '530.16': 'Entropy & Information Theory',
  '530.43': 'Plasma Physics',
  '530.44': 'Superconductivity',
  '531.11': 'Laws of Motion',
  '531.14': 'Energy & Work',
  '531.2': 'Statics',
  '531.4': 'Friction',
  '532.05': 'Fluid Dynamics',
  '532.1': 'Hydrostatics',
  '532.2': 'Hydrodynamics',
  '532.52': 'Turbulence',
  '532.53': 'Laminar Flow',
  '534.5': 'Ultrasonics',
  '534.8': 'Vibrations',
  '535.5': 'Polarization of Light',
  '535.6': 'Color',
  '535.84': 'Spectroscopy',
  '535.85': 'Lasers',
  '536.3': 'Radiation & Thermometry',
  '536.5': 'Temperature',
  '537.62': 'Ferromagnetism',
  '537.622': 'Magnetic Properties of Materials',
  '537.63': 'Electromagnetism',
  '539.14': 'Nuclear Reactions',
  '539.3': 'Mechanics of Solids',
  '539.6': 'Molecular Physics',
  '540.7': 'Chemistry Education',
  '541.1': 'Quantum Chemistry',
  '541.22': 'Chemical Bonding',
  '541.24': 'Chemical Bonds',
  '541.39': 'Chemical Kinetics',
  '541.4': 'Colloid Chemistry',
  '542.3': 'Chromatography',
  '542.4': 'Spectroscopy',
  '542.5': 'Electroanalytical Methods',
  '543.4': 'Separation',
  '543.6': 'Mass Spectrometry',
  '546.4': 'Alkaline-Earth Metals',
  '546.5': 'Transition Metals',
  '546.7': 'Metals',
  '547.3': 'Aromatic Compounds',
  '551.4': 'Geomorphology',
  '551.7': 'Historical Geology & Stratigraphy',
  '551.9': 'Geochemistry',
  '571.1': 'Cells & Organisms',
  '571.2': 'Cell Biology',
  '571.3': 'Anatomy',
  '571.4': 'Biophysics & Physiology',
  '571.5': 'Ecology',
  '571.7': 'Biological Control & Regulation',
  '571.9': 'Diseases',
  '572.65': 'Enzymes',
  '572.7': 'Carbohydrates',
  '575.1': 'Genetics',
  '575.2': 'Mutations',
  '576.82': 'Natural Selection',
  '577.2': 'Ecosystem Dynamics',
  '577.6': 'Aquatic Ecology',
  '579.2': 'Viruses',
  '579.3': 'Bacteria',

  // ── 600s Technology decimal subdivisions ─────────────────────
  '604.2': 'Technical Drawing',
  '610.73': 'Nursing',
  '612.7': 'Musculoskeletal System',
  '615.8': 'Physical Therapies',
  '615.9': 'Toxicology',
  '616.02': 'Emergency Medicine',
  '616.07': 'Pathology',
  '616.89': 'Mental Disorders & Psychiatry',
  '620.0042': 'Engineering Design',
  '620.00423': 'Computer-Aided Design',
  '620.1123': 'Elasticity',
  '620.1124': 'Plasticity',
  '620.1125': 'Creep',
  '620.1126': 'Fracture Mechanics',
  '620.1127': 'Fatigue',
  '620.12': 'Properties of Structural Materials',
  '621.1': 'Steam Engineering',
  '621.12': 'Boilers & Furnaces',
  '621.16': 'Combustion Engineering',
  '621.2': 'Hydraulic & Pneumatic Engineering',
  '621.25': 'Hydraulic Machinery',
  '621.315': 'Electrical Wiring',
  '621.389': 'Noise Control',
  '621.392': 'Logic Circuits',
  '621.3952': 'Digital Signal Processing',
  '621.4022': 'Thermodynamic Cycles',
  '621.815': 'Machine Elements',
  '624.1821': 'Reinforced Concrete',
  '624.1826': 'Prestressed Concrete',
  '624.151': 'Soil Mechanics',
  '624.15136': 'Foundation Engineering',
  '624.16': 'Rock Mechanics',
  '624.6': 'Water Structures',
  '625.1': 'Railroads',
  '625.7': 'Roads & Highways',
  '629.43': 'Unmanned Spacecraft',
  '631.5': 'Crop Production',
  '641.3': 'Food',
  '641.5': 'Cooking',
  '651.74': 'Business Writing',
  '658.01': 'Philosophy of Management',
  '658.02': 'Business Administration',
  '658.04': 'Project Management',
  '658.12': 'Business Ethics & Corporate Governance',
  '658.312': 'Training & Development',
  '658.4057': 'Leadership',
  '658.408': 'Corporate Social Responsibility',
  '658.4092': 'Leadership Skills',
  '658.5145': 'TQM & Six Sigma',
  '665.5': 'Petroleum Refining',
  '665.7': 'Natural Gas',
  '669.2': 'Nonferrous Metals',
  '678.2': 'Natural Rubber',
  '678.3': 'Synthetic Rubber',
  '681.2': 'Scientific Instruments',

  // ── 700s Arts decimal subdivisions ────────────────────────────
  '701.1': 'Fine Arts & Society',
  '701.15': 'Psychology of Aesthetics',
  '701.17': 'Art Criticism',
  '701.18': 'Artists',
  '701.2': 'Fine Arts as a Profession',
  '701.3': 'Communication in Fine Arts',
  '701.4': 'Special Topics in Fine Arts',
  '704.9': 'Iconography',
  '704.94': 'Subjects in Painting',
  '709.01': 'Arts of Ancient Times',
  '709.02': 'Medieval Art',
  '709.03': 'Renaissance to Modern Art',
  '709.04': 'Modern Art (20th Century)',
  '709.05': 'Contemporary Art (21st Century)',
  '709.5': 'Art of Asia',
  '709.54': 'Art of South Asia & India',
  '709.6': 'Art of Africa',
  '709.7': 'Art of North America',
  '709.8': 'Art of South America',
  '741.2': 'Drawing Techniques',
  '741.5': 'Comics, Cartoons & Caricatures',
  '741.59': 'Comics by Country',
  '741.5973': 'American Comics & Graphic Novels',
  '741.5944': 'French Comics (Bande Dessinée)',
  '741.5952': 'Manga',
  '745.1': 'Antiques',
  '745.2': 'Industrial Design',
  '745.5': 'Handicrafts',
  '745.54': 'Textile Crafts',
  '746.4': 'Needlework',
  '759.1': 'Painting of North America',
  '759.2': 'Painting of Great Britain',
  '759.4': 'French Painting',
  '759.5': 'Italian Painting',
  '759.6': 'Spanish Painting',
  '759.9': 'Painting of Other Areas',
  '778.5': 'Cinematography & Videography',
  '781.2': 'Elements of Music',
  '781.4': 'Techniques of Music',
  '781.5': 'Kinds of Music',
  '781.6': 'Traditions of Music',
  '781.62': 'Traditional Music',
  '781.64': 'Electronic Music',
  '781.65': 'Jazz',
  '781.66': 'Rock Music',
  '781.7': 'Sacred Music',
  '782.1': 'Opera',
  '782.4': 'Songs',
  '782.42': 'Popular Songs',
  '784.2': 'Orchestras',
  '791.4': 'Films & Motion Pictures',
  '791.43': 'Movies',
  '791.44': 'Radio',
  '791.45': 'Television',
  '792.6': 'Musical Theater',
  '792.7': 'Variety Shows & Circus',
  '793.2': 'Parties',
  '793.3': 'Dancing',
  '793.73': 'Puzzles',
  '793.8': 'Magic',
  '794.1': 'Chess',
  '796.3': 'Ball Games',
  '796.33': 'Football & Soccer',
  '796.332': 'American Football',
  '796.334': 'Soccer',
  '796.34': 'Racket Games',
  '796.342': 'Tennis',
  '796.35': 'Ball Driven by Club or Bat',
  '796.352': 'Golf',
  '796.357': 'Baseball',
  '796.36': 'Cricket',
  '796.4': 'Weight Lifting & Related Sports',
  '796.5': 'Outdoor Life Activities',
  '796.52': 'Mountaineering',
  '796.6': 'Cycling',
  '796.7': 'Motorsports',
  '796.8': 'Combat Sports',
  '796.83': 'Boxing',
  '796.9': 'Ice & Snow Sports',

  // ── 800s Literature decimal subdivisions ─────────────────────
  '801.95': 'Literary Criticism',
  '808.1': 'Poetry — Rhetoric',
  '808.2': 'Drama — Rhetoric',
  '808.3': 'Fiction — Rhetoric',
  '808.4': 'Essays — Rhetoric',
  '808.5': 'Speaking & Oral Rhetoric',
  '808.51': 'Public Speaking & Oratory',
  '808.53': 'Debates',
  '808.56': 'Conversation',
  '808.6': 'Letters — Rhetoric',
  '808.7': 'Humor — Rhetoric',
  '808.8': 'Literature — Collections',
  '809.1': 'History of Poetry',
  '809.2': 'History of Drama',
  '809.3': 'History of Fiction',

  // American Literature
  '811.1': 'American Poetry, Colonial',
  '811.3': 'American Poetry, 1800–1861',
  '811.4': 'American Poetry, 1861–1900',
  '811.52': 'American Poetry, 1900–1945',
  '811.54': 'American Poetry, 1945–1999',
  '811.6': 'American Poetry, 2000–',
  '812.54': 'American Drama, 1945–1999',
  '812.6': 'American Drama, 2000–',
  '813.1': 'American Fiction, Colonial',
  '813.3': 'American Fiction, 1800–1861',
  '813.4': 'American Fiction, 1861–1900',
  '813.52': 'American Fiction, 1900–1945',
  '813.54': 'American Fiction, 1945–1999',
  '813.6': 'American Fiction, 2000–',

  // English Literature
  '821.1': 'Old English Poetry',
  '821.2': 'Middle English Poetry',
  '821.3': 'Elizabethan Poetry',
  '821.4': 'Early 17th Century Poetry',
  '821.5': 'Restoration Poetry',
  '821.6': '18th Century English Poetry',
  '821.7': 'Romantic English Poetry',
  '821.8': 'Victorian Poetry',
  '821.912': 'English Poetry, 1900–1945',
  '821.92': 'English Poetry, 1945–1999',
  '821.914': 'English Poetry, 2000–',
  '822.33': 'Shakespeare',
  '822.912': 'English Drama, 1900–1945',
  '822.914': 'English Drama, 2000–',
  '823.1': 'English Fiction, Medieval',
  '823.3': 'English Fiction, Elizabethan',
  '823.4': 'English Fiction, Restoration',
  '823.5': 'English Fiction, 18th Century',
  '823.6': 'English Fiction, early 19th Century',
  '823.7': 'English Fiction, Romantic Era',
  '823.8': 'English Fiction, Victorian',
  '823.912': 'English Fiction, 1900–1945',
  '823.92': 'English Fiction, 1945–1999',
  '823.914': 'English Fiction, 2000–',

  // Other literatures
  '891.1': 'Sanskrit Literature',
  '891.2': 'Pali Literature',
  '891.3': 'Persian Literature',
  '891.4': 'Hindi & Related Literatures',
  '891.43': 'Hindi Fiction',
  '891.431': 'Hindi Short Stories',
  '891.44': 'Hindi Poetry',
  '891.45': 'Hindi Drama',
  '891.46': 'Urdu Literature',
  '891.47': 'Bengali Literature',
  '891.48': 'Marathi Literature',
  '891.49': 'Tamil Literature',
  '891.5': 'Iranian Literature',
  '891.6': 'Celtic Literature',
  '891.7': 'Russian Literature',
  '891.73': 'Russian Fiction',
  '891.74': 'Russian Drama',
  '891.8': 'Slavic Literature',
  '892.4': 'Hebrew Literature',
  '892.7': 'Arabic Literature',
  '895.1': 'Chinese Literature',
  '895.13': 'Chinese Fiction',
  '895.6': 'Japanese Literature',
  '895.63': 'Japanese Fiction',
  '895.635': 'Japanese Short Stories',
  '895.64': 'Japanese Poetry',
  '895.7': 'Korean Literature',

  // ── 900s History & Geography decimal subdivisions ─────────────
  '909.07': 'Medieval History',
  '909.08': 'Modern History (1500–)',
  '909.09': 'History of Specific Areas',
  '910.2': 'Geography',
  '910.4': 'Accounts of Travel',
  '910.91': 'Geography of Ancient World',
  '914.1': 'British Isles',
  '914.2': 'British Isles',
  '914.4': 'France',
  '914.5': 'Italy',
  '914.6': 'Iberian Peninsula',
  '914.9': 'Other Parts of Europe',
  '915.4': 'South Asia',
  '915.41': 'India',
  '915.42': 'Pakistan',
  '915.43': 'Bangladesh',
  '915.44': 'Sri Lanka',
  '915.6': 'Middle East',
  '915.61': 'Turkey',
  '915.67': 'Iraq',
  '915.69': 'Israel & Palestine',
  '917.3': 'United States',
  '951.05': 'History of China (People\'s Republic)',
  '951.54': 'History of Tibet',
  '954.02': 'Mughal Period',
  '954.03': 'British India',
  '954.04': 'India since 1947',
  '954.1': 'History of Northeast India',
  '954.5': 'History of West India',
  '954.8': 'History of South India',
  '954.9': 'History of Other Parts of South Asia',
  '973.1': 'Early Colonial Period',
  '973.2': 'Colonial Period',
  '973.3': 'American Revolution',
  '973.4': 'Constitutional Period',
  '973.5': 'Antebellum Period',
  '973.6': 'Civil War Period',
  '973.7': 'Reconstruction',
  '973.8': 'Gilded Age',
  '973.9': 'United States since 1900',
  '973.91': 'United States 1900–1945',
  '973.92': 'United States since 1945',

  // ── Deep decimal names — fixes remaining "Further sub-topics" ──

  // 003 Systems Simulation
  '003.852': 'Discrete Event Simulation',
  '003.857': 'Chaos Theory & Complex Systems',

  // 005 Computer Software (section renamed to avoid collision with 005.1)
  '005.109': 'History of Computer Programming',
  '005.16': 'Computer Programming Methodology',

  // 181 Eastern Philosophy
  '181.1': 'Chinese Philosophy (Confucianism, Taoism)',
  '181.2': 'Japanese Philosophy',
  '181.4': 'Indian & Hindu Philosophy',
  '181.45': 'Buddhist Philosophy',
  '181.48': 'Jain Philosophy',

  // 230 Christian Theology
  '230.0': 'Christian Theology — Special Topics',
  '230.04': 'Christianity & Interfaith Relations',
  '230.08': 'Christianity for Specific Groups',

  // 305 Social Groups
  '305.40': 'Women — Special Studies',
  '305.42': 'Women in Society',
  '305.420': 'Women in Society — General Works',
  '305.80': 'Racial & Ethnic Groups — General Works',
  '305.800': 'Race & Ethnicity — Standard Topics',
  '305.8009': 'Race & Ethnicity — Geographic Treatment',
  '305.80095': 'Race & Ethnicity in Asia',
  '305.800954': 'Race & Ethnicity in India',

  // 306 Culture & Institutions
  '306.80': 'Family & Marriage — General Works',
  '306.83': 'Unmarried & Single-Parent Families',
  '306.87': 'Marriage & Married Couples',
  '306.872': 'Husbands & Wives',
  '306.874': 'Parent-Child Relations',
  '306.8743': 'Parent-Child Dynamics',

  // 320 Political Science
  '320.9': 'Political Science — Geographic Treatment',
  '320.94': 'Politics of Europe',
  '320.95': 'Politics of Asia',
  '320.954': 'Politics of India',

  // 322 State & Organizations
  '322.42': 'Political Parties & Government',
  '322.420': 'Political Parties — General Works',

  // 330 Economics
  '330.0': 'Economics — General Works & Theory',
  '330.01': 'Philosophy & Theory of Economics',
  '330.015': 'Mathematical & Quantitative Economics',
  '330.019': 'Psychology of Economics',
  '330.09': 'History of Economics',
  '330.092': 'Economists (Biography)',
  '330.9': 'Economics — Geographic Treatment',
  '330.91': 'Economics — World',
  '330.95': 'Economics of Asia',
  '330.954': 'Economics of India',
  '330.973': 'Economics of the United States',

  // 338 Production Economics
  '338.90': 'Economic Development — General Works',
  '338.900': 'Economic Development — Theory',
  '338.91': 'Economic Planning',
  '338.92': 'Economic Aid',
  '338.927': 'Development Aid & Foreign Assistance',
  '338.95': 'Economic Development in Asia',
  '338.954': 'Economic Development in India',

  // 363 Environmental & Social Problems
  '363.70': 'Environmental Issues — General',
  '363.72': 'Water Pollution',
  '363.73': 'Air & Environmental Pollution',
  '363.738': 'Atmospheric Pollution & Climate',
  '363.73874': 'Climate Change & Greenhouse Gases',
  '363.738746': 'Carbon Emissions',
  '363.739': 'Land Pollution & Solid Waste',

  // 515 Mathematical Analysis
  '515.24': 'Series & Sequences',
  '515.243': 'Power Series & Convergence',
  '515.2433': 'Fourier Series',

  // 530 Physics
  '530.0': 'Physics — General Works',
  '530.01': 'Philosophy & Theory of Physics',
  '530.076': 'Physics Exercises & Problems',
  '530.09': 'History of Physics',
  '530.092': 'Physicists (Biography)',

  // 532 Fluid Mechanics
  '532.051': 'Inviscid Flow',
  '532.0527': 'Turbulence',
  '532.059': 'Vortex Motion',

  // 616 Diseases
  '616.0': 'Diseases — General Works & Pathology',
  '616.001': 'General Medical Philosophy',
  '616.002': 'Medical Reference Works',
  '616.025': 'Emergency Medical Care',
  '616.044': 'Molecular Pathology',
  '616.0472': 'Medical Imaging',
  '616.075': 'Medical Diagnosis & Testing',
  '616.079': 'Immunology',

  // 620 Engineering
  '620.10': 'Materials Science — General Works',
  '620.001': 'Engineering — Philosophy & Theory',
  '620.0015': 'Mathematical Methods in Engineering',
  '620.004': 'Engineering Design & Systems',

  // 658 Management
  '658.409': 'Executive Management — Geographic Treatment',
  '658.4093': 'Management in Europe',
  '658.4094': 'Management in Asia',

  // 741 Comics & Graphic Arts
  '741.594': 'European Comics',
  '741.5941': 'British Comics',
  '741.5942': 'Franco-Belgian Comics (Bande Dessinée)',
  '741.595': 'Asian Comics',
  '741.597': 'North American Comics',

  // 793 Indoor Games
  '793.7': 'Games, Puzzles & Quizzes',
  '793.74': 'Card Games',

  // 808 Rhetoric
  '808.0': 'Writing — General Works & Style',
  '808.02': 'Writing Composition & Style Guides',
  '808.042': 'Scientific & Technical Writing',
  '808.06': 'Professional & Business Writing',
  '808.066': 'Technical Communication by Field',

  // 811 American Poetry
  '811.5': 'American Poetry, 20th Century',
  '811.50': 'American Poetry — General (20th Century)',
  '811.509': 'History & Criticism of American Poetry',

  // English Literature modern groups
  '821.91': 'English Poetry, Modern (1900–)',
  '821.910': 'English Poetry, Early 20th Century — General',
  '823.91': 'English Fiction, Modern (1900–)',
  '823.910': 'English Fiction, Early 20th Century — General',

  // ── Missing first-decimal chips for high-traffic sections ─────
  // These fix "Further sub-topics" labels for the most-used DDC
  // numbers in this library's catalog.

  // 001 Knowledge
  '001.0': 'Knowledge — General Works',
  '001.9': 'Controversial Knowledge',

  // 003 Systems
  '003.3': 'Mathematical Modelling',
  '003.5': 'Systems Analysis & Design',
  '003.7': 'Systems Engineering',
  '003.8': 'Systems Simulation',

  // 025 Library Operations
  '025.0': 'Library Operations — General',
  '025.1': 'Library Administration',
  '025.3': 'Bibliographic Analysis',
  '025.4': 'Subject Analysis & Cataloguing',
  '025.5': 'Library Services to Users',
  '025.8': 'Library Maintenance',

  // 028 Reading
  '028.5': 'Reading for Children & Young Adults',
  '028.9': 'Reading Interests',

  // 123 Determinism
  '123.3': 'Free Will',
  '123.5': 'Chance & Probability (Philosophy)',

  // 133 Paranormal
  '133.3': 'Divination',
  '133.4': 'Demonology & Witchcraft',
  '133.5': 'Astrology',
  '133.8': 'Psychic Phenomena',
  '133.9': 'Spiritualism',

  // 174 Occupational Ethics
  '174.2': 'Medical Ethics',
  '174.4': 'Legal Ethics',
  '174.9': 'Ethics of Other Professions',

  // 179 Other Ethical Norms
  '179.1': 'Cruelty & Kindness',
  '179.6': 'Animal Rights',
  '179.8': 'Respect for Life',
  '179.9': 'Other Ethical Norms',

  // 201 Religious Mythology
  '201.3': 'Social Theology',
  '201.6': 'Religion & Science',

  // 204 Religious Experience
  '204.2': 'Mysticism',
  '204.3': 'Meditation',
  '204.4': 'Prayer & Contemplation',

  // 261 Church & Society
  '261.5': 'Christianity & Science',
  '261.8': 'Christianity & Social Problems',

  // 291 Comparative Religion
  '291.2': 'Mythology',
  '291.3': 'Theology & Doctrine',
  '291.4': 'Religious Experience',

  // 299 Other Religions
  '299.3': 'Shamanism',
  '299.7': 'New Age Religions',
  '299.9': 'Other Religions & Beliefs',

  // 301 Sociology
  '301.0': 'Sociology — General Works',
  '301.1': 'Sociological Theory',
  '301.2': 'Culture & Society',
  '301.3': 'Social Institutions',
  '301.4': 'Social Problems',
  '301.5': 'Social Change',

  // 305 Groups of People
  '305.0': 'Social Groups — General Works',
  '305.9': 'Other Social Groups',

  // 306 Culture & Society
  '306.0': 'Culture & Society — General Works',
  '306.1': 'Cultural Theory',

  // 322 State & Organizations
  '322.1': 'State & Religious Organizations',
  '322.2': 'State & Economic Organizations',
  '322.4': 'State & Political Parties',

  // 325 Migration & Colonization
  '325.3': 'Colonization',
  '325.5': 'Emigration',

  // 327 International Relations
  '327.2': 'Diplomacy',
  '327.5': 'Foreign Policy & Power',

  // 331 Labor Economics
  '331.0': 'Labor Economics — General',
  '331.6': 'Workers by Racial & Ethnic Groups',

  // 332 Financial Economics
  '332.0': 'Financial Economics — General Works',
  '332.9': 'Financial Institutions — Other',

  // 333 Land Economics
  '333.2': 'Land Use',
  '333.3': 'Land Reform',

  // 336 Public Finance
  '336.5': 'Government Revenue',
  '336.7': 'Government Expenditures',

  // 338 Production Economics
  '338.0': 'Production Economics — General Works',
  '338.8': 'Industrial Combinations & Monopoly',

  // 342 Constitutional Law
  '342.5': 'Constitutional Rights & Freedoms',
  '342.7': 'Civil Rights & Liberties',

  // 343 Military Law
  '343.5': 'Tax Law',
  '343.7': 'Military Law',

  // 346 Private Law
  '346.5': 'Property Law',
  '346.7': 'Contracts & Obligations',

  // 351 Public Administration
  '351.0': 'Public Administration — General',
  '351.1': 'Government Policy',
  '351.5': 'Civil Service',
  '351.8': 'Government Functions',

  // 352 General Public Administration
  '352.2': 'Local Government Administration',
  '352.3': 'Municipal Administration',
  '352.7': 'Financial Administration',

  // 355 Military Science
  '355.0': 'Military Science — General Works',
  '355.8': 'Military Equipment & Weapons',

  // 361 Social Services
  '361.1': 'Social Problems',
  '361.2': 'Social Work',
  '361.9': 'History of Social Services',

  // 363 Social Problems & Services
  '363.1': 'Public Safety',
  '363.3': 'Public Order & Security',
  '363.4': 'Censorship',
  '363.6': 'Public Utilities',
  '363.9': 'Population Problems',

  // 372 Primary Education
  '372.3': 'Science in Primary Education',
  '372.4': 'Reading in Primary Education',
  '372.6': 'Language Arts in Primary Education',
  '372.7': 'Mathematics in Primary Education',
  '372.9': 'History of Primary Education',

  // 378 Higher Education
  '378.0': 'Higher Education — General Works',
  '378.2': 'Academic Degrees & Scholarships',

  // 381 Commerce
  '381.0': 'Commerce — General Works',
  '381.1': 'Retail Trade',
  '381.4': 'Services Industries',

  // 382 International Trade
  '382.0': 'International Trade — General',
  '382.3': 'Tariffs & Customs',
  '382.7': 'Trade Agreements',

  // 388 Roads & Highways
  '388.1': 'Roads',
  '388.3': 'Traffic Engineering',
  '388.4': 'Urban Transportation',

  // 393 Death Customs
  '393.1': 'Funerary Customs',
  '393.9': 'Other Death Customs',

  // 414 Phonology
  '414.6': 'Prosody & Versification',
  '414.8': 'Writing Systems',

  // 415 Grammar
  '415.0': 'Grammar — General Works',
  '415.6': 'Tense, Aspect & Mood',
  '415.9': 'Other Grammatical Topics',

  // 417 Historical Linguistics
  '417.2': 'Etymology',
  '417.7': 'Historical & Comparative Linguistics',

  // 428 English Usage
  '428.0': 'English Language — General Works',
  '428.3': 'English Composition',

  // 501 Philosophy of Science
  '501.4': 'Scientific Methods',
  '501.9': 'Science & Society',

  // 502 Miscellany of Science
  '502.0': 'Science — Miscellany',
  '502.3': 'Science Dictionaries',
  '502.8': 'Science Tools & Equipment',

  // 507 Education in Science
  '507.2': 'Science Research',
  '507.8': 'Science Experiments & Projects',

  // 509 History of Science
  '509.2': 'Biography of Scientists',
  '509.5': 'History of Science by Region',

  // 511 Foundations of Mathematics
  '511.0': 'Mathematical Logic — General Works',
  '511.7': 'Computability & Algorithms',

  // 513 Arithmetic
  '513.2': 'Fractions & Decimals',
  '513.3': 'Ratio & Proportion',
  '513.5': 'Number Systems',
  '513.8': 'Business Mathematics',
  '513.9': 'Elementary Algebra',

  // 516 Geometry
  '516.0': 'Geometry — General Works',
  '516.4': 'Projective Geometry',
  '516.7': 'Affine Geometry',
  '516.8': 'Finite Geometry',
  '516.9': 'Non-Euclidean Geometry',

  // 517 Calculus
  '517.0': 'Calculus — General Works',
  '517.2': 'Differential Calculus',
  '517.3': 'Integral Calculus',
  '517.5': 'Advanced Calculus',
  '517.6': 'Vector Calculus',
  '517.7': 'Complex Variables',
  '517.8': 'Special Functions',
  '517.9': 'Calculus of Variations',

  // 518 Numerical Analysis
  '518.0': 'Numerical Analysis — General',
  '518.2': 'Numerical Methods',
  '518.6': 'Numerical Linear Algebra',

  // 519 Probability & Statistics
  '519.8': 'Stochastic Processes',
  '519.9': 'Other Topics in Applied Mathematics',

  // 522 Astronomy Techniques
  '522.6': 'Spectroscopy in Astronomy',
  '522.8': 'Telescopes',

  // 531 Classical Mechanics
  '531.0': 'Classical Mechanics — General Works',
  '531.7': 'Rigid Body Mechanics',

  // 533 Gas Mechanics
  '533.6': 'Aerodynamics',
  '533.7': 'Compressible Flow',

  // 538 Magnetism
  '538.6': 'Magnetic Materials',
  '538.7': 'Electromagnetism',

  // 548 Crystallography
  '548.0': 'Crystallography — General Works',
  '548.5': 'Crystal Structures',
  '548.7': 'Crystal Physics',
  '548.8': 'Crystal Growth',

  // 549 Mineralogy
  '549.1': 'Systematic Mineralogy',
  '549.5': 'Optical Mineralogy',

  // 551 Geology & Geophysics
  '551.0': 'Geology — General Works',
  '551.8': 'Sedimentology',

  // 552 Petrology
  '552.1': 'Igneous Rocks',
  '552.2': 'Metamorphic Rocks',
  '552.5': 'Sedimentary Rocks',

  // 553 Economic Geology
  '553.1': 'Coal',
  '553.6': 'Non-metallic Minerals',
  '553.7': 'Water Resources',

  // 573 Specific Physiological Processes
  '573.7': 'Biological Signalling',
  '573.8': 'Nervous System Functions',

  // 574 Ecology
  '574.0': 'Ecology — General Works',
  '574.1': 'Biomes & Ecosystems',
  '574.5': 'Ecological Relationships',
  '574.8': 'Natural History',

  // 579 Microorganisms
  '579.1': 'Microbiology Techniques',
  '579.5': 'Fungi',
  '579.8': 'Algae',

  // 591 Animal Biology
  '591.1': 'Anatomy of Animals',
  '591.3': 'Physiology of Animals',
  '591.5': 'Animal Behaviour & Ecology',

  // 599 Mammals
  '599.8': 'Primates',
  '599.9': 'Other Mammals',

  // 614 Public Health
  '614.1': 'Epidemiology',
  '614.5': 'Communicable Disease Control',

  // 617 Surgery
  '617.7': 'Ophthalmology',
  '617.9': 'Surgical Specialties',

  // 618 Obstetrics & Pediatrics
  '618.1': 'Gynecology',
  '618.2': 'Obstetrics',
  '618.9': 'Pediatrics',

  // 623 Military Engineering
  '623.1': 'Military Science & Weapons',
  '623.5': 'Ordnance & Ballistics',
  '623.8': 'Nautical Engineering',

  // 624 Civil Engineering
  '624.0': 'Civil Engineering — General Works',
  '624.3': 'Soil Mechanics & Earthwork',

  // 631 Agriculture Techniques
  '631.4': 'Soil Science',
  '631.7': 'Irrigation & Drainage',

  // 634 Orchards & Fruits
  '634.5': 'Berries & Grapes',
  '634.9': 'Forestry',

  // 662 Fuels
  '662.6': 'Gaseous Fuels',
  '662.8': 'Liquid Fuels & Petroleum',

  // 666 Ceramics
  '666.8': 'Advanced Ceramics',
  '666.9': 'Special Ceramics',

  // 668 Organic Products Technology
  '668.3': 'Plastic & Synthetic Materials',
  '668.4': 'Polymers & Plastics',
  '668.5': 'Adhesives & Coatings',
  '668.9': 'Other Organic Products',

  // 669 Metallurgy
  '669.3': 'Copper & Copper Alloys',
  '669.4': 'Aluminum & Aluminum Alloys',

  // 671 Metalworking
  '671.2': 'Foundry Work & Casting',
  '671.3': 'Welding & Cutting',
  '671.5': 'Forming & Shaping',
  '671.7': 'Surface Treatment & Finishing',
  '671.8': 'Fastening & Joining',

  // 681 Precision Instruments
  '681.4': 'Measuring Instruments',
  '681.7': 'Optical Instruments',

  // 692 Auxiliary Construction
  '692.5': 'Scaffolding & Shoring',
  '692.8': 'Demolition',

  // 693 Masonry
  '693.1': 'Brick & Tile Work',
  '693.5': 'Concrete & Plastering',
  '693.7': 'Stone & Block Work',
  '693.8': 'Reinforced Concrete Construction',

  // 701 Philosophy of Fine Arts
  '701.0': 'Philosophy of Art — General Works',
  '701.8': 'Creativity & Aesthetics',

  // 709 Art History
  '709.0': 'Art History — General Works',
  '709.2': 'Biography of Artists',

  // 725 Public Buildings
  '725.2': 'Governmental Buildings',
  '725.5': 'Commercial & Industrial Buildings',

  // 779 Photographs
  '779.0': 'Photography — General Works',
  '779.2': 'Portraits & Figure Studies',

  // 792 Stage Presentations
  '792.0': 'Theatre — General Works',
  '792.8': 'Ballet & Dance',

  // American Literature decimal sub-groups
  '813.0': 'American Fiction — General & Critical Works',
  '813.5': 'American Fiction, 20th Century',
  '813.9': 'American Fiction — Miscellaneous',
  '814.3': 'American Essays, 19th Century',
  '814.5': 'American Essays, 20th Century',
  '814.6': 'American Essays, 2000–',
  '818.3': 'American Miscellaneous Writings, 19th Century',
  '818.4': 'American Miscellaneous Writings, early 20th Century',
  '818.5': 'American Miscellaneous Writings, 1945–1999',
  '818.6': 'American Miscellaneous Writings, 2000–',

  // English Literature decimal sub-groups
  '821.0': 'English Poetry — General & Critical Works',
  '821.9': 'English Poetry, 20th–21st Century',
  '822.2': 'Elizabethan & Jacobean Drama',
  '822.3': 'Shakespeare',
  '822.9': 'English Drama, 20th–21st Century',
  '823.0': 'English Fiction — General & Critical Works',
  '823.9': 'English Fiction, 20th–21st Century',
  '824.8': 'English Essays, Victorian',
  '824.9': 'English Essays, 20th–21st Century',
  '828.8': 'English Miscellaneous Writings, Victorian',
  '828.9': 'English Miscellaneous Writings, 20th–21st Century',

  // German Literature
  '833.6': 'German Fiction, 20th Century',
  '833.9': 'German Fiction, 21st Century',

  // French Literature
  '843.7': 'French Fiction, 19th Century',
  '843.8': 'French Fiction, 20th Century',
  '843.9': 'French Fiction, 21st Century',

  // Spanish Literature
  '851.1': 'Italian Poetry, Early',
  '851.9': 'Italian Poetry, 20th Century',
  '863.6': 'Spanish Fiction, 20th Century',
  '863.7': 'Spanish Fiction, 21st Century',

  // Other Literatures (894.8x = South Asian language literatures)
  '894.3': 'Caucasian Language Literatures',
  '894.8': 'South Asian Language Literatures',
  '894.81': 'Bengali Literature',
  '894.82': 'Marathi Literature',
  '894.83': 'Gujarati Literature',
  '894.84': 'Punjabi Literature',
  '894.9': 'Other South & Southeast Asian Literatures',

  // World History
  '809.0': 'Literary History — General Works',
  '809.8': 'Literature of Specific Groups',
  '809.9': 'Literature by Period',
  '909.0': 'World History — General Works',
  '909.8': 'History of Specific Groups',

  // Biography
  '922.2': 'Biography — Individual',
  '922.9': 'Collected Biography',
  '923.2': 'Biography of Scientists',
  '923.8': 'Biography of Statesmen',

  // History of India — regional subdivisions
  '954.0': 'History of India — General & Ancient',
  '954.2': 'History of Rajasthan & Northwest India',
  '954.3': 'History of Gujarat & Western India',
  '954.4': 'History of Uttar Pradesh & Central India',
  '954.7': 'History of Madhya Pradesh & Central India',

  // History of China
  '951.0': 'History of China — General',
  '951.5': 'History of Taiwan',

  // Geography of Asia
  '915.0': 'Geography of Asia — General',
  '915.1': 'Geography of China',

  // ── Literature — period subdivisions ─────────────────────────
  '819.1': 'Canadian Poetry in English',
  '819.2': 'Canadian Fiction in English',

  // ── Religion ─────────────────────────────────────────────────
  '291.1': 'Sacred Texts — Comparative Religion',
  '294.5927': 'Hindu Epics — Ramayana & Mahabharata',
  '299.93': 'New Religious Movements',
  '299.936': 'New Age & Esoteric Movements',
  '230.086': 'Christian Theology for Young People',

  // ── Mathematics ───────────────────────────────────────────────
  '510.2462': 'Mathematics for Engineers',

  // ── Physics ───────────────────────────────────────────────────

  // ── Chemistry ─────────────────────────────────────────────────
  '541.0421': 'Quantum Chemistry',

  // ── Engineering ───────────────────────────────────────────────
  '620.105': 'Materials Testing',
  '620.106': 'Engineering Materials — Applications',
  '629.8312': 'Digital Control Systems',

  // ── Social Sciences & Economics ───────────────────────────────
  '300.72': 'Social Science Research Methods',
  '650.1': 'Personal Success in Business',

  // ── Comics & Visual Arts ──────────────────────────────────────

  // ── History & Geography ───────────────────────────────────────

  // ── Round-2 deep names — high-traffic "Further sub-topics" chips ──

  // Film & Cinema (791.43x)
  '791.430': 'Film — General Works & Theory',
  '791.4301': 'Film Theory & Aesthetics',
  '791.4302': 'Film Criticism & History',
  '791.4308': 'Films for Specific Audiences',
  '791.4309': 'Film History by Country',
  '791.433': 'Documentary & Educational Films',
  '791.436': 'Animated Films',
  '791.437': 'Film Genres (Comedy, Drama, Action…)',

  // Heat Transfer (621.40x)
  '621.402': 'Heat Transfer',
  '621.4023': 'Convective Heat Transfer',
  '621.4025': 'Heat Exchangers',
  '621.406': 'Thermodynamic Cycles',

  // Applied Mechanics & Materials sub-levels (620.1x)
  '620.13': 'Engineering Properties of Materials',
  '620.15': 'Fatigue, Fracture & Corrosion',
  '620.19': 'Composite, Nano & Smart Materials',

  // Hindi Literature (891.43x)
  '891.430': 'Hindi Fiction — General & Collections',
  '891.432': 'Hindi Short Stories',
  '891.433': 'Hindi Novels',
  '891.439': 'Hindi Prose Fiction — Other',

  // Algebra (512.8x)
  '512.81': 'Linear & Multilinear Algebra',
  '512.86': 'Category Theory & Homological Algebra',
  '512.87': 'Commutative & Homological Algebra',
  '512.89': 'Abstract Algebra — Special Topics',

  // Structural Engineering (624.17x)
  '624.174': 'Structural Design & Loads',
  '624.175': 'Structural Stability & Buckling',
  '624.176': 'Finite Element Analysis',
  '624.177': 'Structural Dynamics & Earthquake Engineering',

  // Health Services (362.1x)
  '362.10': 'Health Care — General Works',
  '362.17': 'Nursing & Long-Term Care',
  '362.19': 'Health Services by Medical Specialty',
  '362.196': 'Community Health Services',

  // Nervous System & Senses (612.8x)
  '612.80': 'Neurophysiology — General Works',
  '612.82': 'Brain & Cerebral Physiology',
  '612.83': 'Sensory & Peripheral Nervous System',
  '612.84': 'Vision & Eye Physiology',
  '612.85': 'Hearing & Auditory Physiology',

  // Technology & Social Change (303.48x)
  '303.482': 'Technology as Agent of Social Change',
  '303.483': 'Social Impact of Technology',
  '303.484': 'Impact of Communications Technology',

  // Quantum & Gauge Field Theory (530.14x)
  '530.141': 'Classical Field Theory',
  '530.142': 'Quantum Field Theory',
  '530.143': 'Gauge Theory & Symmetry Groups',
  '530.144': 'String Theory & M-Theory',

  // British India (954.03x)
  '954.031': 'Early British Period (1757–1857)',
  '954.035': 'British Raj (1858–1947)',

  // Sociology — Theory & History (301.0x)
  '301.01': 'Theory & Philosophy of Sociology',
  '301.07': 'Sociological Research Methods',
  '301.09': 'History of Sociology',

  // Machine Engineering (621.8x)
  '621.81': 'Machine Elements & Components',
  '621.85': 'Power Transmission & Gearing',
  '621.89': 'Tribology, Lubrication & Bearings',

  // Marathi Literature (894.827x)
  '894.8271': 'Marathi Poetry',
  '894.8272': 'Marathi Drama',
  '894.8273': 'Marathi Fiction',
  '894.8274': 'Marathi Prose & Essays',

  // Electrical Power Distribution (621.31x, 621.319x)
  '621.310': 'Electrical Power — General Works',
  '621.317': 'Power Measurement & Instrumentation',
  '621.3191': 'Power Cables & Overhead Lines',
  '621.3192': 'Power Transformers',
  '621.3124': 'AC/DC Generators & Motors',

  // Higher Education (378.1x)
  '378.11': 'Teaching Staff & Faculty',
  '378.16': 'Student Financial Aid',
  '378.19': 'Student Services & Campus Life',

  // Financial Economics (332.0x)
  '332.01': 'Financial Theory',
  '332.02': 'Money, Banking & Financial Instruments',
  '332.04': 'International Finance',
  '332.09': 'History of Finance',

  // Mathematics Education (510.7x)
  '510.71': 'Mathematics Teaching & Curriculum',
  '510.76': 'Mathematics — Problems & Exercises',

  // Functional Analysis / Hilbert Spaces (515.72x)
  '515.722': 'Banach & Metric Spaces',
  '515.723': 'Hilbert Space Theory',
  '515.724': 'Linear Operators on Hilbert Spaces',

  // Geomorphology & Physical Geography (551.4x)
  '551.48': 'Hydrology & Water Resources',
  '551.49': 'Glaciology & Ice',

  // Political Parties — deep geographic levels (322.420x)
  '322.4209': 'Political Parties — Historical & Geographic Treatment',
  '322.42095': 'Political Parties in Asia',
  '322.420954': 'Political Parties in India',
  '322.4209548': 'Indian Political Organizations',
  '322.42095484': 'Indian Political Organizations (State Level)',

  // Other Languages (490x)
  '491.45': 'Marathi',
  '491.49': 'Sindhi & Other Dardic Languages',

  // Urban Sociology (307.7x)
  '307.76': 'Urban Communities & City Life',

  // Systems Modeling (003.5x)
  '003.54': 'Systems Modeling',

  // Hindi Poetry (891.44x)
  '891.441': 'Classical Hindi Poetry',
  '891.443': 'Modern Hindi Poetry',

  // Psychological Schools (150.1x)
  '150.19': 'Psychological Schools & Systems',

  // Printing (686.2x)
  '686.2': 'Typography & Printing Technology',

  // Culture — Geographic (306.0x)
  '306.09': 'Cultural Studies — Geographic Treatment',

  // Thermodynamics (536.7x)
  '536.7': 'Laws of Thermodynamics',
  '536.71': 'Entropy & the Second Law',
  '536.72': 'Free Energy & Thermodynamic Potentials',
  '536.73': 'Statistical Thermodynamics',

  // Engineering Materials (620.10x, 620.106x, 620.112x)
  '620.1064': 'Fatigue & Fracture of Materials',
  '620.1129': 'Mechanical Testing of Materials',

  // Decision Analysis (658.401x)
  '658.4012': 'Decision Analysis & Modelling',
  '658.4013': 'Risk & Uncertainty Management',

  // Imagination & Creative Thinking (153.3x)
  '153.35': 'Creative Thinking & Problem Solving',

  // Special Electrical Engineering (621.36x)
  '621.362': 'Photovoltaics & Solar Cells',
  '621.365': 'Plasma & Ion Technology',
  '621.367': 'Lasers & Electro-optics',

  // Soil Mechanics (624.151x)
  '624.1513': 'Bearing Capacity & Settlement',
  '624.1519': 'Special Foundation Problems',

  // Steel Structures — sub-levels (624.1834x)
  '624.18340': 'Steel Connections & Joints',
  '624.18341': 'Steel Beams & Columns',
  '624.18342': 'Steel Plate Structures',

  // Medical Technology (610.28)
  '610.28': 'Medical Instruments & Technology',

  // Illustration & Graphic Design (741.6x)
  '741.6': 'Illustration & Graphic Design',

  // Power Systems Engineering (621.312x)

  // Probability & Statistics — deep (519.5x)
  '519.50': 'Statistics — General Works',
  '519.502': 'Statistical Theory',
  '519.509': 'History of Statistics',

  // Operations Research (519.6x, 519.7x)
  '519.60': 'Operations Research — General',
  '519.703': 'Linear Programming',

  // Differential Equations (515.3x)
  '515.350': 'Differential Equations — General',
  '515.354': 'Elliptic & Parabolic Equations',
  '515.355': 'Hyperbolic Equations',
  '515.356': 'Nonlinear PDEs',
  '515.357': 'Integral Equations',

  // Calculus (515.1x, 515.2x)
  '515.10': 'Calculus — General Works',
  '515.13': 'Multivariable & Vector Calculus',
  '515.14': 'Algebraic Topology',
  '515.15': 'Numerical Methods in Calculus',

  // Number Theory (512.7x)
  '512.70': 'Number Theory — General Works',

  // Graph Theory (511.5x)
  '511.50': 'Graph Theory — General Works',
  '511.52': 'Directed Graphs',
  '511.56': 'Hypergraphs & Special Graphs',

  // Discrete Mathematics (511.3x)
  '511.30': 'Combinatorics — General Works',

  // Control Systems (629.8x)
  '629.80': 'Control Engineering — General Works',
  '629.801': 'Control Systems Theory',
  '629.8032': 'Feedback Control Systems',

  // Signal Processing (621.382x, 621.38x)
  '621.3823': 'Signal Detection & Estimation',
  '621.38224': 'Speech & Audio Processing',
  '621.38228': 'Image & Video Processing',

  // Semiconductors & Electronics (621.381x)
  '621.38156': 'Optoelectronics',
  '621.38158': 'Power Electronics',

  // Electromagnetics (621.3x)
  '621.30': 'Electrical Engineering — General',
  '621.301': 'Electrical Engineering Theory',
  '621.3011': 'Electromagnetic Theory',
  '621.302': 'Electrical Standards & Instruments',

  // Fluid Mechanics deep (532.0x, 532.5x)
  '532.052': 'Viscous Flow',
  '532.053': 'Boundary Layer Theory',
  '532.058': 'Compressible Flow',
  '532.59': 'Multiphase Flow',

  // Solid Mechanics (531.x)
  '531.10': 'Mechanics — General Works',
  '531.12': 'Kinematics',
  '531.13': 'Dynamics',
  '531.15': 'Constraints & Equilibrium',
  '531.16': 'Lagrangian & Hamiltonian Mechanics',

  // Molecular Physics (539.x)
  '539.10': 'Atomic Physics — General',
  '539.13': 'Nuclear Forces & Scattering',
  '539.17': 'Elementary Particle Physics',
  '539.19': 'Condensed Matter at Atomic Scale',

  // Quantum Mechanics (530.12x)
  '530.120': 'Quantum Mechanics — General Works',
  '530.121': 'Quantum Mechanics Foundations',
  '530.122': 'Wave Mechanics & Schrödinger Equation',
  '530.124': 'Spin & Angular Momentum',
  '530.126': 'Perturbation Theory',
  '530.128': 'Quantum Optics',

  // Management Science (658.4x beyond what was added)
  '658.4038': 'Information Technology Management',

  // Computer Networks (004.6x)
  '004.60': 'Computer Networks — General Works',
  '004.61': 'Network Architecture',
  '004.682': 'Network Security',

  // Database (005.74x)
  '005.740': 'Database Systems — General',
  '005.748': 'NoSQL & Big Data Stores',
  '005.749': 'Data Warehousing',

  // Software Engineering (005.1x)
  '005.102': 'Software Engineering Practice',
  '005.103': 'Object-Oriented Programming',
  '005.105': 'Concurrent & Distributed Programming',
  '005.106': 'Functional Programming',
  '005.107': 'Scripting Languages',
  '005.108': 'Programming Environments',

  // Artificial Intelligence (006.3x)
  '006.30': 'Artificial Intelligence — General Works',
  '006.38': 'Robotics AI',

  // Architecture (720.x)
  '720.9': 'History of Architecture',
  '720.92': 'Architects (Biography)',

  // Urban Planning (711.x)
  '711.40': 'Urban Design — General Works',
  '711.42': 'City Planning',
  '711.43': 'Urban Renewal',

  // Construction (690.x, 693.x beyond existing)
  '693.10': 'Concrete Construction — General',
  '693.12': 'Precast & Prestressed Concrete',

  // Chemical Engineering (660.x)
  '660.20': 'Chemical Engineering Operations',
  '660.281': 'Reactor Design',
  '660.2844': 'Distillation',
  '660.2848': 'Absorption & Extraction',

  // Environmental Engineering (628.x)
  '628.10': 'Water Supply Engineering — General',
  '628.13': 'Groundwater & Wells',
  '628.16': 'Water Purification',
  '628.162': 'Water Treatment Processes',
  '628.168': 'Wastewater Treatment',
  '628.30': 'Environmental Pollution Control',
  '628.36': 'Solid Waste Management',
  '628.44': 'Solid Waste Disposal',
  '628.46': 'Recycling',

  // Geotechnical (624.15x beyond existing)
  '624.153': 'Deep Foundations & Piles',
  '624.154': 'Retaining Structures',
  '624.159': 'Underground Construction',

  // Surveying & GIS (526.x)
  '526.10': 'Geodesy & Surveying — General',
  '526.102': 'Geodetic Surveying',
  '526.109': 'Remote Sensing',

  // Economics of Development (338.9x)

  // Public Administration (351.x)
  '351.00': 'Public Administration — General',
  '351.009': 'Comparative Public Administration',
  '351.0094': 'Public Administration in Europe',
  '351.0095': 'Public Administration in Asia',
  '351.00954': 'Public Administration in India',

  // Law (340.x)
  '340.50': 'Legal History — General',
  '340.52': 'Legal History by Country',
  '340.9': 'Geographic Treatment of Law',
  '340.90': 'Comparative Law',
  '340.954': 'Law of India',

  // Education (370.x)
  '370.10': 'Philosophy of Education',
  '370.19': 'Education — Sociological Aspects',
  '370.90': 'Education — Historical Treatment',
  '370.92': 'Educators (Biography)',

  // Management (658.x beyond existing)
  '658.049': 'International Business Management',
  '658.05': 'Information Systems Management',
  '658.056': 'e-Business & Digital Management',

  // Marketing (658.8x)
  '658.80': 'Marketing — General Works',
  '658.802': 'Marketing Research',
  '658.812': 'Customer Relations (CRM)',

  // Accounting (657.x)
  '657.30': 'Financial Accounting — Special Topics',
  '657.301': 'Accounting Theory',
  '657.302': 'Accounting Standards',
  '657.309': 'Accounting History',

  // Psychology sub-levels (150.x)
  '150.10': 'Psychology — General Works',
  '150.195': 'Psychoanalytic Psychology',
  '150.198': 'Humanistic & Existential Psychology',

  // Cognitive Science (153.x)
  '153.10': 'Conscious Mental Processes',
  '153.12': 'Memory',
  '153.40': 'Thought, Thinking & Reasoning',
  '153.42': 'Critical Thinking',
  '153.60': 'Communication',

  // Social Psychology (302.x)
  '302.20': 'Communication — General Works',
  '302.22': 'Visual Communication',
  '302.2314': 'Books & Reading',
  '302.234': 'Television',
  '302.2312': 'Radio Broadcasting',

  // Biotechnology (660.6x)
  '660.60': 'Biotechnology — General Works',
  '660.65': 'Pharmaceutical Biotechnology',

  // Genetics (576.5x, 591.5x)
  '576.50': 'Genetics — General Works',
  '576.52': 'Genomics',
  '576.53': 'Epigenetics',

  // Ecology (577.x)
  '577.10': 'Ecology — General & Theory',
  '577.14': 'Ecosystem Functions',
  '577.16': 'Community Ecology',
  '577.18': 'Evolutionary Ecology',
  '577.22': 'Aquatic Ecology',
  '577.26': 'Agroecology',
  '577.27': 'Marine Ecology',

  // Plant Science (580.x, 632.x)
  '581.60': 'Useful Plants',
  '581.63': 'Plants & Environment',
  '581.68': 'Ethnobotany',

  // India History deep (954.x)
  '954.030': 'British India — General Works',
  '954.036': 'Indian Independence Movement',
  '954.038': 'Partition of India (1947)',
  '954.041': 'Nehru Era (1947–1964)',
  '954.042': 'Post-Nehru India (1964–1977)',
  '954.044': 'India since 1984',
  '954.045': 'India — Contemporary (1991–)',
  '954.046': 'India in the 21st Century',

  // ── Round-3: fix remaining name-collision fallbacks ────────────

  // Marathi sub-class (needed so chip at /discover/894.82 isn't "Marathi Literature")
  '894.827': 'Marathi Prose Fiction',
  '894.82730': 'Marathi Novels — General Works',

  // Steel Structures sub-class (chip at /discover/624.183)
  '624.1834': 'Steel Structural Members & Connections',

  // Chemical Engineering (660.2x)
  '660.29': 'Chemical Technology — General Works',

  // Social Classes
  '305.51': 'Upper Classes & Elites',
  '305.512': 'Working Class',
  '305.513': 'Middle Class',

  // Personal Finance (332.02x)
  '332.024': 'Personal Finance & Investment',

  // Applied Math deep (510.24x)
  '510.24': 'Mathematics for Scientists & Engineers',
  '510.246': 'Numerical Mathematics for Engineers',

  // Geology of Asia (555.x)
  '555.4': 'Geology of Southeast Asia',
  '555.44': 'Geology of South Asia',

  // FEM applications (624.176x)
  '624.1762': 'Finite Element Analysis — Applications',

  // Manufacturing (670.4x)
  '670.42': 'Computer-Aided & Automated Manufacturing',

  // Technical Writing (808.04x)
  '808.04': 'Writing for Specific Fields & Disciplines',

  // Steam Engineering (621.1x)
  '621.15': 'Steam Turbines & Boilers',

  // Printing Processes (686.22x)
  '686.22': 'Printing & Reproduction Processes',

  // Integral Equations (515.6x)
  '515.64': 'Variational Methods & Integral Equations',

  // Shakespeare (822.33x)

  // Climate & Greenhouse (363.7387x)
  '363.7387': 'Global Warming & Climate Change',

  // Multilinear Algebra (512.94x)
  '512.94': 'Tensor & Exterior Algebra',

  // Organic Chemistry sub-level (547.0x)
  '547.0': 'Organic Chemistry — General Theory',

  // Radar (621.3845x)
  '621.3845': 'Radar Systems',

  // Distillation (660.2842x)
  '660.2842': 'Distillation & Rectification',

  // Women — historical (305.4209x)
  '305.4209': 'Women — Historical & Geographic Treatment',

  // Technology & specific cultures (306.461x)
  '306.461': 'Impact of Specific Technologies on Culture',

  // R&D and innovation (658.575x)
  '658.575': 'Innovation & New Product Development',

  // Factor economics (338.04x)
  '338.04': 'Production Factors (Land, Labour, Capital)',

  // Materials science advanced (620.118x)
  '620.118': 'Non-Destructive Testing of Materials',

  // Leadership (658.4063x)
  '658.4063': 'Leadership & Motivation',

  // South India geography (954.84x)
  '954.84': 'Karnataka',
  '954.87': 'Kerala',
  '954.82': 'Tamil Nadu',

  // Occultism (299x)

  // Folk Literature (398.20x)
  '398.20': 'Folk Literature — General Works',

  // Celestial Bodies (523.0x)
  '523.0': 'Celestial Bodies — General Works & Astrophysics',
  '523.01': 'Astrophysics of Stars & Galaxies',

  // Specific Cancers (616.994x)
  '616.994': 'Specific Cancers & Tumors',

  // Robot programming (629.831x)
  '629.831': 'Robot Design & Programming',

  // Comedy Films (791.4372x)
  '791.4372': 'Comedy Films',

  // Portuguese/Brazilian Fiction (869.3x)
  '869.3': 'Portuguese & Brazilian Fiction',

  // Chemical Thermodynamics sub-levels (541.3x)
  '541.30': 'Chemical Thermodynamics — General',
  '541.31': 'Chemical Equilibrium',
  '541.32': 'Electrochemistry',
  '541.34': 'Surface Chemistry',

  // Signal processing sub-levels (621.382x)

  // Radio & Radar sub-levels (621.384x)
  '621.3841': 'Antenna & Wave Propagation',
  '621.3842': 'Radio Transmitters & Receivers',
  '621.3843': 'Microwave Engineering',

  // Materials science sub-levels (620.11x)
  '620.115': 'Electrical Properties of Materials',

  // Sequences & series of functions (515.6x)
  '515.62': 'Functional Analysis — Bases & Sequences',
  '515.63': 'Tensor Analysis',

  // Production Economics (338.0x)
  '338.01': 'Theory of Production',
  '338.09': 'Production Economics — History',
  '338.06': 'Efficiency & Productivity',

  // Digital circuits (621.395x)
  '621.3950': 'Digital Circuits — General',
  '621.3951': 'Logic Circuits',

  // Power electronics (621.317x beyond existing)
  '621.3174': 'Electrical Measurements',
  '621.3175': 'Power System Protection',

  // Environmental geography (551.5x)
  '551.50': 'Meteorology — General Works',
  '551.52': 'Climatology',
  '551.53': 'Atmospheric Optics',
  '551.58': 'Climatic Regions',

  // Mineralogy (549.x)
  '549.10': 'Mineralogy — General Works',
  '549.12': 'Crystallography',
  '549.13': 'Physical Mineralogy',

  // Petrology (552.x)
  '552.10': 'Igneous Rocks',
  '552.14': 'Volcanic Rocks',
  '552.3': 'Metamorphic Rocks',

  // Remote sensing (526.9x)
  '526.9': 'Remote Sensing & Photogrammetry',
  '526.98': 'Satellite Imagery & GIS',

  // Sanskrit Literature (891.2x)
  '891.20': 'Sanskrit Literature — General Works',
  '891.21': 'Sanskrit Poetry',
  '891.22': 'Sanskrit Drama',
  '891.23': 'Sanskrit Prose',

  // Telugu Literature (894.8x)
  '894.823': 'Telugu Literature',
  '894.826': 'Kannada Literature',
  '894.8261': 'Kannada Fiction',
  '894.8262': 'Kannada Poetry',

  // Tamil Literature (894.811x)
  '894.8112': 'Modern Tamil Poetry',
  '894.8113': 'Tamil Fiction',

  // Bengali Literature (891.44x for Bengali — not Hindi)
  '891.444': 'Hindi Drama',

  // Urdu (891.43x — Urdu Fiction)

  // Indian English Literature (820.x)
  '820.80': 'English Literature — Anthologies',
  '820.809': 'English Literature — Historical Collections',
  '820.8009': 'History of English Literature — Collections',

  // Shakespeare plays (822.33x)
  '822.332': 'Shakespeare — Histories',
  '822.333': 'Shakespeare — Tragedies',
  '822.334': 'Shakespeare — Comedies',

  // Urban Planning (711.x deeper)
  '711.401': 'City Planning Theory',
  '711.405': 'Sustainable Urban Design',
  '711.408': 'Urban Planning for Specific Groups',

  // Irrigation & Water (627.x)
  '627.10': 'Hydraulic Engineering — General',
  '627.12': 'River Engineering',
  '627.13': 'Flood Control',
  '627.14': 'Dams & Reservoirs',
  '627.15': 'Canal Engineering',

  // Transportation (629.04x)
  '629.046': 'Transportation Safety',
  '629.049': 'Transportation Systems',

  // Aeronautics (629.1x)
  '629.10': 'Aeronautics — General Works',
  '629.134': 'Aircraft Design',

  // Propulsion (621.43x)
  '621.430': 'Internal Combustion Engines — General',
  '621.435': 'Gas Turbines',
  '621.436': 'Jet Engines & Rocket Propulsion',

  // Refrigeration (621.56x)
  '621.56': 'Refrigeration & Heat Pumps',
  '621.565': 'Air Conditioning Systems',

  // Welding (671.5x)
  '671.52': 'Fusion Welding',
  '671.521': 'Arc Welding',
  '671.522': 'Gas Welding',

  // Casting (671.2x)
  '671.20': 'Casting — General Works',
  '671.25': 'Die Casting',
  '671.27': 'Investment Casting',

  // Robotics & Automation (629.8x deeper)
  '629.832': 'Robotic Arms & Manipulators',
  '629.833': 'Robot Sensors',
  '629.835': 'Robot Control Systems',

  // Communications (384.x)
  '384.30': 'Telecommunication Systems — General',
  '384.33': 'Telephone Systems',
  '384.35': 'Mobile Communications',

  // India — States (954.x deeper)
  '954.80': 'South India — General History',
  '954.81': 'Andhra Pradesh & Telangana',
  '954.83': 'Goa',
  '954.85': 'Tamil Nadu',
  '954.86': 'Kerala (pre-state)',
  '954.89': 'Sri Lanka',
  '954.90': 'History of East India',
  '954.91': 'Bangladesh',
  '954.92': 'West Bengal',
  '954.93': 'Odisha',

  // Islamic Studies (297.x)
  '297.0': 'Islam — General Works',
  '297.09': 'History of Islam',
  '297.092': 'Islamic Biography',
  '297.12': 'Quran — Study & Interpretation',
  '297.8': 'Islamic Sects',

  // Buddhism (294.3x deeper)
  '294.30': 'Buddhism — General Works',
  '294.309': 'History of Buddhism',
  '294.31': 'Buddhist Scriptures',
  '294.33': 'Buddhist Worship & Meditation',
  '294.361': 'Theravada Buddhism',
  '294.362': 'Mahayana Buddhism',
  '294.363': 'Vajrayana & Tibetan Buddhism',

  // Christian denominations (280.x)
  '280.0': 'Christian Denominations — General',
  '280.01': 'Ecumenical Movement',
  '280.4': 'Protestant Churches',
  '280.42': 'Lutheran',
  '280.44': 'Methodist',
  '280.46': 'Baptist',
  '280.48': 'Pentecostal',

  // Philosophy of Science (501.x)
  '501.0': 'Philosophy of Science — General Works',
  '501.01': 'Scientific Method',
  '501.09': 'History of Philosophy of Science',

  // Logic (160.x)
  '160.0': 'Philosophical Logic — General',
  '160.09': 'History of Logic',

  // Phenomenology (142.x)
  '142.7': 'Phenomenology',
  '142.78': 'Existentialism',

  // Political Philosophy (320.01x)
  '320.010': 'Political Philosophy — General',
  '320.011': 'Political Theory',
  '320.015': 'Political Science — Mathematical Models',
  '320.019': 'Political Psychology',

  // Legislative (328.x)
  '328.40': 'Parliament — Legislative Procedure',
  '328.73': 'United States Congress',

  // Constitutional Law (342.x)
  '342.02': 'Constitutional Law — General Principles',
  '342.09': 'Judicial Review',

  // International Law (341.x)
  '341.20': 'International Organizations',
  '341.23': 'United Nations',
  '341.26': 'International Treaties',

  // Industrial Engineering (658.5x)
  '658.500': 'Production Management — General',
  '658.501': 'Production Management Theory',
  '658.503': 'Supply Chain Management',
  '658.506': 'Quality Assurance',
  '658.5621': 'ISO Standards',

  // Logistics (658.78x)
  '658.781': 'Inventory Management',
  '658.782': 'Warehousing',
  '658.784': 'Transportation Logistics',

  // Finance & Accounting (332.x deeper)
  '332.10': 'Banks & Banking — General',
  '332.11': 'Central Banking',
  '332.17': 'Credit & Loans',
  '332.18': 'Microfinance',
  '332.60': 'Investments — General Works',
  '332.67': 'Portfolio Management',

  // Statistics (519.5x deeper)
  '519.531': 'Regression Analysis',
  '519.532': 'Time Series Analysis',
  '519.534': 'Multivariate Analysis',
  '519.536': 'Non-Parametric Statistics',
  '519.538': 'Bayesian Statistics',

  // Combinatorics (511.6x)
  '511.60': 'Combinatorics — General',
  '511.61': 'Enumeration & Counting',
  '511.62': 'Ramsey Theory',
  '511.66': 'Extremal Combinatorics',

  // ── Round-4: break remaining name-collision cascades ──────────

  // Numerical methods in engineering (620.00151)
  '620.00151': 'Numerical Methods & Finite Element Methods',

  // Bengali / South Asian Literatures (894.9x)
  '894.92': 'Bengali Literature',
  '894.929': 'Bengali — Other Genres',

  // Computer Software sub-levels (005.2x, 005.27x)
  '005.27': 'Application Software',
  '005.276': 'Mobile & Web Apps',
  '005.453': 'Real-Time Operating System Design',

  // Technology & Society deep (303.4833)
  '303.4833': 'Artificial Intelligence & Social Change',

  // Working Class sub-levels
  '305.5122': 'Working Class — Occupational Studies',
  '305.568': 'Working Class — Regional & Ethnic Studies',

  // Service industries (338.47)
  '338.47': 'Service Industries (Tertiary Sector)',

  // History of Mathematics by person (510.92)
  '510.92': 'Mathematicians (Biography)',

  // Electronics sub-levels (621.38x)
  '621.380': 'Electronics — General Works',
  '621.3804': 'Electronic Components — Standards',

  // Design theory (745.40)
  '745.40': 'Design — Theory & Criticism',

  // Film in Asia & India (791.4309x)
  '791.43095': 'Asian Cinema',
  '791.430954': 'Indian Cinema (Bollywood & Regional)',

  // Russian Fiction (891.733)
  '891.733': 'Russian Novels',
  '891.730': 'Russian Fiction — General Works',

  // British Raj sub-period (954.0350)
  '954.0350': 'Early British Raj (1858–1905)',
  '954.0355': 'Late British India (1905–1947)',

  // Sociology regional (301.095)
  '301.095': 'Sociology in Asia',
  '301.0954': 'Sociology in India',

  // Culture regional (306.095)
  '306.095': 'Cultural Studies in Asia',
  '306.0954': 'Cultural Studies in India',

  // Sexual relations (306.76)
  '306.76': 'Sexuality & Society',
  '306.762': 'Heterosexuality',
  '306.764': 'LGBTQ+ Studies',
  '306.766': 'Gender & Sexual Identity',

  // Algebra general (512.0x)
  '512.0': 'Algebra — General Works',
  '512.02': 'Fundamental Algebraic Concepts',

  // Crystal Growth (548.83)
  '548.83': 'Crystal Growth & Nucleation',

  // Advertising methods (658.827)
  '658.827': 'Advertising Media & Campaigns',

  // Programming techniques (005.11x)
  '005.117': 'Programming Paradigms & Styles',

  // Age groups (305.23)
  '305.23': 'Youth & Children in Society',
  '305.235': 'Teenagers & Adolescents',

  // Econometrics (330.0151)
  '330.0151': 'Econometrics',

  // Student aid sub-levels (378.166)
  '378.166': 'Scholarships & Fellowships',
  '378.199': 'Student Activities & Organizations',
  '378.1990': 'Student Organizations — Special Topics',
  '378.19909': 'Student Organizations — Geographic Treatment',
  '378.199095': 'Student Organizations in Asia',

  // Marathi novel cascade
  '894.827301': 'Contemporary Marathi Fiction',
  '894.8273010': 'Marathi Fiction — Specific Works',

  // Electronics base (621.38 section)

  // Applied arts deeper

  // Organic chemistry sub-level (547.0x)
  '547.02': 'Organic Reactions & Mechanisms',
  '547.05': 'Spectroscopic Analysis of Organic Compounds',
  '547.06': 'Synthetic Organic Chemistry',
  '547.07': 'Computational Organic Chemistry',

  // Computational sub-topics (620.0015x)
  '620.00152': 'Computational Fluid Dynamics',
  '620.00153': 'Optimization in Engineering',
  '620.00154': 'Simulation Methods',

  // Algorithms (005.1x)

  // Statistics (519.5 sub-levels beyond what was added)

  // Cultural History of Technology (306.46x)
  '306.462': 'Social Effects of Specific Technologies',

  // Food Science (641.5x, 664.x)
  '641.50': 'Cooking — General Works',
  '641.509': 'History of Cooking',

  // India — modern politics (324.254x)
  '324.254': 'Indian Political Parties',
  '324.2548': 'Indian National Congress & BJP',

  // Business ethics (174.4x)

  // Accounting sub-levels (657.x)
  '657.41': 'Cost Accounting',
  '657.42': 'Budgeting',

  // Educational Technology (371.33x)
  '371.3350': 'Online Learning',
  '371.3358': 'Blended Learning',

  // Special education (371.9x)
  '371.90': 'Special Education — General Works',
  '371.91': 'Gifted Students',
  '371.92': 'Learning Disabilities',
  '371.95': 'Inclusive Education',

  // Gender studies (305.3x)
  '305.30': 'Gender — General Works',
  '305.31': 'Men in Society',
  '305.32': 'Masculinity',
  '305.36': 'Transgender & Non-Binary Studies',

  // Environmental law & policy (344.046x)
  '344.046': 'Environmental Law',
  '344.0462': 'Air & Water Pollution Law',
  '344.0463': 'Wildlife Conservation Law',

  // ── Round-5: remaining collision fixes ────────────────────────

  // Neurology/Mental Health
  '616.85': 'Mental Health & Psychiatric Disorders',
  '616.852': 'Anxiety & Depression',
  '616.858': 'Addiction & Substance Abuse',
  '616.86': 'Substance Disorders',
  '616.87': 'Neurological Disorders',

  // India — Ancient Period
  '954.05': 'Medieval India (Sultanate & Vijayanagara)',

  // Mysticism sub-level
  '204.22': 'Contemplative Mysticism',

  // English Grammar for specific groups
  '428.24': 'English Grammar for Non-Native Speakers',
  '428.25': 'English for Academic Purposes',
  '428.26': 'Advanced English Usage',
  '428.27': 'English Vocabulary Building',

  // Regression analysis sub-level

  // Physics education (530.07)
  '530.07': 'Physics — Study Guides & Exercises',

  // Classical Mechanics sub-levels
  '531.01': 'Mechanics — Theory & Principles',

  // Elementary particles
  '539.721': 'Quarks, Leptons & Gauge Bosons',
  '539.725': 'Mesons & Hadrons',
  '539.726': 'Neutrinos',

  // Physical Chemistry general
  '541.0': 'Physical Chemistry — General Works',
  '541.00': 'Physical Chemistry — Standard Subdivisions',

  // Semiconductor advanced (621.3810x)
  '621.38104': 'MOSFET & Field-Effect Devices',
  '621.38106': 'Bipolar Junction Transistors',

  // Aircraft lift & performance (629.1323)
  '629.1323': 'Aerodynamic Lift & Drag',
  '629.1324': 'Aircraft Propulsion Performance',

  // Bengali literature sub-levels
  '894.811': 'Bengali Poetry',
  '894.812': 'Bengali Drama',
  '894.813': 'Bengali Fiction',

  // School testing & evaluation (371.26)
  '371.26': 'Educational Tests & Examinations',
  '371.262': 'Standardised Testing',
  '371.27': 'Grading & Assessment',

  // Sociolinguistics sub-level
  '401.93': 'Language & Social Context',
  '401.94': 'Language Contact & Bilingualism',

  // Tensor algebra cascade
  '512.943': 'Tensor Algebra',
  '512.9434': 'Exterior Algebra & Differential Forms',

  // Algebraic Geometry sub-levels (516.35x)
  '516.352': 'Algebraic Curves',
  '516.353': 'Algebraic Surfaces',
  '516.355': 'Algebraic Varieties',

  // Molecular Chemistry (541.22)
  '541.224': 'Orbital Theory',
  '541.226': 'Molecular Spectroscopy',

  // Biology techniques (570.28x)
  '570.28': 'Biological Laboratory Methods',
  '570.285': 'Bioinformatics & Computational Biology',

  // Human factors (620.82)
  '620.82': 'Human Factors Engineering',
  '620.820': 'Ergonomics — Applications',

  // Powder metallurgy
  '669.94': 'Powder Metallurgy — Specific Metals',
  '669.95': 'Refractory Metals',
  '669.96': 'Rare Earth Metals',

  // German Fiction (833.9x)
  '833.91': 'Contemporary German Fiction',
  '833.92': 'German Fantasy & Science Fiction',

  // Urdu literature
  '891.460': 'Urdu Fiction',
  '891.461': 'Classical Urdu Poetry',
  '891.463': 'Modern Urdu Fiction',

  // British India cascade break
  '954.03509': 'Colonial India — Historical Events',
  '954.035092': 'Personalities of British India',
  '954.0350954': 'British Raj in South Asia',

  // Ontology sub-level (111.8x)
  '111.85': 'Ontology — Identity & Persistence',
  '111.83': 'Space & Time in Ontology',
  '111.84': 'Causality in Ontology',

  // Hindu Vedic literature (294.53)
  '294.53': 'Vedic & Upanishadic Literature',
  '294.531': 'Rigveda',
  '294.532': 'Samaveda & Yajurveda',
  '294.535': 'Vedic Upanishads',

  // Economics of Asia deeper (330.95x)
  '330.9549': 'Economics of South Asia',
  '330.9598': 'Economics of Indonesia',
  '330.96': 'Economics of Africa',

  // Marathi Fiction cascade (894.8273010x)
  '894.82730108': 'Marathi Short Stories & Collections',

  // Student organizations cascade
  '378.1990954': 'Student Organizations in India',

  // Scientific instruments (502.8x)
  '502.85': 'Computers in Science',

  // Research methods (001.4x)
  '001.40': 'Research Methods — General',

  // Biography (920.x)
  '920.0': 'Biography — Collections',
  '920.02': 'Multiple Biographies',
  '920.04': 'Collective Biography by Region',
  '920.09': 'Biography by Period',
  '920.092': 'Biographies by Profession',

  // Industrial Relations (331.x)
  '331.10': 'Labour Economics — General',
  '331.11': 'Supply & Demand for Labour',
  '331.12': 'Job Market & Employment',
  '331.13': 'Unemployment & Underemployment',
  '331.20': 'Compensation & Pay',
  '331.21': 'Wages & Salaries',
  '331.25': 'Employee Benefits',
  '331.40': 'Labour by Demographic Group',
  '331.41': 'Women in the Workforce',
  '331.42': 'Older Workers',
  '331.44': 'Youth Employment',
  '331.88': 'Trade Unions',

  // Business Law (346.07x)
  '346.073': 'Partnership & Agency',
  '346.078': 'Corporate Law',
  '346.0788': 'Securities Law',

  // Taxation (343.04x)
  '343.04': 'Tax Law',
  '343.052': 'Income Tax',
  '343.055': 'Sales & Value Added Tax',

  // Social Policy (361.x)
  '361.60': 'Social Policy & Planning — General',
  '361.61': 'Government Social Policy',
  '361.65': 'Social Policy Evaluation',

  // Human Rights (323.x)
  '323.01': 'Philosophy of Human Rights',
  '323.09': 'History of Human Rights',
  '323.44': 'Freedom of Expression',

  // Geopolitics (327.1x)
  '327.10': 'International Relations — General Theory',
  '327.11': 'Diplomacy',
  '327.12': 'Espionage & Intelligence',

  // Developmental Psychology (155.x)
  '155.40': 'Child Psychology — General',
  '155.41': 'Infant Psychology',
  '155.42': 'Child Development (2–12)',
  '155.44': 'Adolescent Psychology',
  '155.60': 'Adult Psychology',
  '155.67': 'Psychology of Ageing',

  // Philosophy of Mind (128.x)
  '128.20': 'Consciousness & Mind',
  '128.23': 'Soul & Spirit',

  // Ethics sub-levels (170.x)
  '170.20': 'Ethics — Special Topics',
  '170.24': 'Applied Ethics',
  '170.44': 'Virtues & Vices',

  // Aesthetics (111.85 already added, also 111.0x)
  '111.0': 'Ontology — General Works',

  // Nonlinear Dynamics (515.39x)
  '515.39': 'Dynamical Systems',
  '515.392': 'Chaos Theory',
  '515.394': 'Bifurcation Theory',
  '515.395': 'Strange Attractors',

  // ── Round-6: granular collision fixes ─────────────────────────

  // Ontology sub-level (111.8x)
  '111.8': 'Formal Ontology',
  '111.82': 'Ontology of Time & Change',

  // Integral transforms (517.38)
  '517.38': 'Integral Transforms (Laplace, Fourier…)',
  '517.35': 'Complex Functions',
  '517.36': 'Conformal Mapping',

  // Surface chemistry sub-level (541.345)
  '541.345': 'Catalysis & Adsorption',
  '541.346': 'Colloids & Emulsions',

  // Primates (599.93)
  '599.93': 'Primates',
  '599.935': 'Apes & Great Apes',

  // Brain physiology cascade (612.823x)
  '612.823': 'Cortex & Higher Brain Functions',
  '612.8233': 'Memory & Cognition — Neuroscience',

  // Adaptive signal processing (621.3827)
  '621.3827': 'Adaptive & Array Signal Processing',
  '621.3828': 'Error-Correcting Codes',

  // Financial management sub-levels (658.15x)
  '658.151': 'Cash Flow & Working Capital Management',
  '658.1511': 'Capital Structure & Financing',
  '658.152': 'Investment Appraisal',
  '658.153': 'Mergers & Acquisitions',
  '658.154': 'Corporate Financial Strategy',

  // Leadership models (658.421)
  '658.421': 'Leadership Styles & Models',
  '658.422': 'Organisational Leadership',

  // Mass transfer (660.283)
  '660.283': 'Mass Transfer Operations',

  // Literature by theme (809.9x)
  '809.93': 'Literature by Theme & Movement',

  // American Fiction anthologies (813.08x)
  '813.08': 'American Short Story Collections',
  '813.087': 'American Fiction — Specific Themes',

  // Japan history by period (952.0x)
  '952.0': 'Japan — History by Period',
  '952.02': 'Feudal Japan',
  '952.03': 'Edo Period',
  '952.04': 'Meiji Era & Modernisation',
  '952.05': 'World War II Era',

  // Database systems (005.73x)
  '005.73': 'Database Management Systems',
  '005.735': 'Relational Database Design',
  '005.736': 'Query Languages & SQL',

  // Encyclopedias sub-level (031.0x)
  '031.0': 'Encyclopedias — General Works',
  '031.02': 'Encyclopedias — Specific Subjects',

  // Age groups (305.26x)
  '305.26': 'Older Adults in Society',

  // Women geographic (305.409, 305.42095x)
  '305.409': 'Women — Geographic Treatment',
  '305.42095': 'Women in Asia',
  '305.420954': 'Women in India',

  // Political Science (320.0x)
  '320.0': 'Political Science — General Works',
  '320.01': 'Political Theory & Ideology',

  // Disaster management (363.34x)
  '363.34': 'Disaster Preparedness & Relief',
  '363.348': 'Emergency Management',

  // ODE problems (515.352)
  '515.352': 'Initial & Boundary Value Problems',

  // Differential geometry (516.373)
  '516.373': 'Differential Geometry of Surfaces',
  '516.375': 'Riemannian Geometry',

  // Mathematical biology (570.15x)
  '570.15': 'Mathematical & Theoretical Biology',
  '570.151': 'Biophysics & Biostatistics',

  // Condensed Matter sub-levels (530.41x)
  '530.411': 'Crystalline Solids',
  '530.412': 'Amorphous & Disordered Materials',
  '530.413': 'Magnetic Materials & Magnetism',
  '530.414': 'Superconductivity',
  '530.416': 'Semiconductors (Physics)',
  '530.419': 'Soft Condensed Matter',

  // Technical Communication by field (808.066x)
  '808.0662': 'Technical Writing for Engineers',
  '808.0663': 'Technical Writing for Sciences',
  '808.0665': 'Technical Writing for Medicine',
  '808.0666': 'Legal Writing',
  '808.0668': 'Technical Writing for Business',

  // Biography (920.x)
  '920.03': 'Biography by Region',

  // Human geography & ecology (304.2x)
  '304.20': 'Human Ecology — General Works',
  '304.23': 'Human Impact on Environment',
  '304.25': 'Environmental Influence on Humans',
  '304.27': 'Adaptation to Environment',
  '304.28': 'Natural Disasters & Human Response',

  // Personality psychology (155.2x)
  '155.20': 'Personality — General Works',
  '155.23': 'Trait Theory',
  '155.25': 'Personality Disorders',
  '155.26': 'Personality Assessment',
  '155.28': 'Personality Change',

  // Power generation deeper (621.3124x)
  '621.31243': 'Three-Phase Generators',
  '621.31244': 'Alternators',
  '621.31248': 'DC Generators',

  // Civil Engineering general (624.0x)
  '624.00': 'Civil Engineering — Standards',
  '624.01': 'Civil Engineering Theory & Design',
  '624.02': 'Civil Engineering Materials',
  '624.07': 'Civil Engineering Education',
  '624.09': 'Civil Engineering History',

  // Planning (307.1x)
  '307.12': 'Regional & Community Planning',
  '307.14': 'Social Planning',
  '307.16': 'Economic Planning (Community)',

  // Medical diagnosis (616.075x)
  '616.0752': 'Laboratory Diagnosis',
  '616.0753': 'Radiological Diagnosis',
  '616.0757': 'Pathological Diagnosis',

  // Chemical engineering operations (660.29x)
  '660.292': 'Chemical Safety & Hazards',
  '660.295': 'Industrial Waste Treatment',
  '660.296': 'Pilot-Plant Operations',

  // Data structures (005.7x)
  '005.70': 'Data in Computing — General',
  '005.76': 'Data Mining',

  // Mechanical testing (620.1129x)
  '620.11292': 'Tensile & Compressive Testing',
  '620.11293': 'Hardness & Wear Testing',
  '620.11298': 'Impact & Fracture Testing',

  // ── Round-7: targeted multi-chip page fixes ────────────────────

  // Biography by subject (920.x divisions)
  '921': 'Biography — Philosophers & Intellectuals',
  '922': 'Biography — Religious Leaders',
  '923': 'Biography — Social Scientists & Politicians',
  '925': 'Biography — Scientists',
  '926': 'Biography — Medical Professionals',
  '927': 'Biography — Artists & Musicians',
  '928': 'Biography — Writers & Journalists',

  // Health services by disease (362.196x)
  '362.1962': 'Heart Disease Services',
  '362.1963': 'Cancer Care Services',
  '362.1968': 'Mental Health Services',
  '362.1969': 'Communicable Disease Services',

  // Computer Engineering sub-classes (621.39x)
  '621.390': 'Computer Engineering — General Works',
  '621.397': 'Input/Output Systems',
  '621.398': 'Storage Systems',
  '621.399': 'Embedded & Special-Purpose Computers',

  // Hindi Novels sub-categories (891.4330x)
  '891.4330': 'Hindi Novels — Collections',
  '891.4335': 'Modern Hindi Novels',
  '891.4337': 'Hindi Historical Novels',

  // Energy resources (333.79x)
  '333.790': 'Energy — General Works',
  '333.792': 'Fossil Fuels',
  '333.793': 'Nuclear Energy',
  '333.794': 'Renewable Energy',
  '333.7953': 'Solar Energy',
  '333.7954': 'Wind Energy',

  // Structural Dynamics sub-classes (624.177x)
  '624.1771': 'Earthquake Engineering & Seismic Design',
  '624.1773': 'Wind-Induced Vibrations',
  '624.1776': 'Structural Fatigue & Vibration',

  // Welding sub-classes (671.3x)
  '671.32': 'Gas Cutting & Flame Processes',
  '671.35': 'Arc Welding Processes',
  '671.36': 'Resistance & Spot Welding',
  '671.37': 'Electron-Beam & Laser Welding',

  // Intelligence & Aptitudes (153.9x)
  '153.93': 'Creative & Divergent Thinking',
  '153.94': 'Intelligence Testing & IQ',
  '153.98': 'Multiple Intelligences',

  // Coordination & Control (303.3x)
  '303.34': 'Social Control & Authority',
  '303.37': 'Propaganda & Persuasion',

  // Technology & Society (306.4x)
  '306.43': 'Technology & Social Life',
  '306.45': 'Technology & Politics',

  // Subatomic Particles (539.7x)
  '539.70': 'Nuclear & Particle Physics — General',
  '539.73': 'Atomic Nuclei',
  '539.74': 'Nuclear Reactions & Fission',
  '539.77': 'Accelerators & Detectors',

  // FEM cascade (620.001515, 620.001518)
  '620.001515': 'FEM — Structural Applications',
  '620.001518': 'FEM — Fluid & Thermal Applications',

  // Electronic standards cascade (621.3804x)
  '621.38041': 'Electronic Symbols & Schematic Standards',
  '621.38043': 'Electronic Tolerance & Reliability Standards',

  // Design sub-levels (745.401x)
  '745.401': 'Design History',
  '745.403': 'Design Ethics & Professional Practice',
  '745.407': 'Design Education',
  '745.409': 'Design — Geographic Treatment',

  // Computer Science Fundamentals (004.0x)
  '004.03': 'Computer Science Dictionaries & Encyclopedias',
  '004.05': 'Computer Science Serials & Journals',
  '004.06': 'Computer Science Organisations',
  '004.07': 'Computer Science Education',
  '004.09': 'History of Computer Science',

  // Hindu Worship sub-levels (294.55x)
  '294.551': 'Hindu Rituals & Ceremonies',
  '294.555': 'Hindu Temples & Pilgrimage',

  // Health care sub-levels (362.10x)
  '362.103': 'Health Administration',
  '362.108': 'Health Care for Specific Groups',
  '362.109': 'Health Care — Geographic Treatment',
  '362.1094': 'Health Care in Europe',
  '362.1095': 'Health Care in Asia',
  '362.10954': 'Health Care in India',

  // Group Theory sub-levels (512.2x)
  '512.21': 'Group Representations',
  '512.23': 'Topological Groups & Lie Groups',
  '512.24': 'Finite Groups',

  // Operator theory on Hilbert spaces (515.7242x)
  '515.7242': 'Spectral Theory',
  '515.7246': 'Bounded Linear Operators',
  '515.7248': 'Unbounded Operators',

  // Chemical Kinetics sub-levels (541.39x)
  '541.394': 'Homogeneous Reaction Kinetics',
  '541.395': 'Catalytic Kinetics',

  // Literature collections by form (808.8x)
  '808.81': 'Poetry Collections',
  '808.83': 'Fiction Anthologies',
  '808.88': 'Essays & Prose Collections',
  '808.89': 'Humour & Satire Collections',

  // English Fiction critical (823.0x)
  '823.00': 'English Fiction — History & Criticism',
  '823.01': 'English Fiction — Theory',
  '823.08': 'English Fiction — Anthologies',

  // Production Economics (338.5x)
  '338.50': 'Supply & Demand — Special Topics',
  '338.54': 'Price Policy',
  '338.52': 'Prices',

  // Consumer Economics (339.4x)
  '339.46': 'Consumer Expenditure',
  '339.47': 'Consumer Goods & Services',

  // Engineering materials properties (620.13x)
  '620.136': 'Thermal Properties of Structural Materials',
  '620.137': 'Strength & Mechanical Behaviour of Materials',

  // More Computer Engineering
  '621.393': 'Memory Chips & RAM',
  '621.3962': 'Wi-Fi & Cellular Systems',
  '621.3965': '5G & Next-Generation Networks',

  // Photonics (621.36x)
  '621.363': 'LED & Display Technology',
  '621.3663': 'Optical Fibre Communications',
  '621.3692': 'Solar Photovoltaics',

  // Materials characterisation (620.11x)
  '620.111': 'Mechanical Properties — Statics',
  '620.117': 'Fatigue Properties',
  '620.119': 'Tribology & Surface Properties',

  // Nuclear Engineering (621.48x)
  '621.480': 'Nuclear Engineering — General',
  '621.484': 'Nuclear Reactor Design',
  '621.487': 'Nuclear Waste Management',
  '621.489': 'Radiation Shielding',

  // Environmental science (363.738x)
  '363.7384': 'Air Quality & Smog',
  '363.7385': 'Acid Rain',
  '363.7388': 'Ozone Depletion',

  // Water resources (333.91x)
  '333.910': 'Water Resources — General',
  '333.912': 'Water Availability & Scarcity',
  '333.913': 'Water Quality',
  '333.914': 'Groundwater',
  '333.916': 'Surface Water & Rivers',

  // Land use (333.73x)
  '333.730': 'Land Use — General',
  '333.731': 'Urban Land Use',
  '333.733': 'Agricultural Land',
  '333.737': 'Forest Land',
  '333.738': 'Protected Areas & Parks',

  // Operations management (658.5x)
  '658.53': 'Procurement & Supply',
  '658.54': 'Plant Layout & Facilities',

  // Technology management (658.5x and 658.4x)
  '658.515': 'Inventory Control',

  // Economics of India deeper (330.954x)
  '330.9540': 'Indian Economy — General',
  '330.9541': 'Indian Economy — Historical',
  '330.9543': 'Post-Independence India Economy',
  '330.9544': 'Liberalisation Era India (1991–)',

  // ── Round-8: fix remaining multi-chip pages ────────────────────

  // Comics sub-levels (741.5x)
  '741.50': 'Comics — History & Criticism',
  '741.56': 'Graphic Novels',
  '741.571': 'Comics — Colour & Design Techniques',

  // Philosophy of computing (004.01x)
  '004.015': 'Computability & Complexity Theory',
  '004.019': 'Psychology of Computing & HCI',

  // Probability sub-levels (519.2x)
  '519.20': 'Probability — General Works',
  '519.28': 'Stochastic Processes',
  '519.236': 'Random Variables',

  // Bayesian Statistics sub-levels (519.542x)
  '519.542': 'Bayesian Inference',
  '519.544': 'Bayesian Networks',

  // Condensed matter (530.47)
  '530.47': 'Phase Transitions & Critical Phenomena',

  // Physical chemistry sub-levels (541.0x)
  '541.02': 'Physical Chemistry — Calculations',
  '541.04': 'Physical Chemistry by Environment',
  '541.05': 'Physical Chemistry — Instruments',

  // Medical technology sub-levels (610.284x)
  '610.284': 'Surgical Instruments',
  '610.285': 'Diagnostic Imaging Equipment',

  // Mechanical testing deep (620.1129x)
  '620.11295': 'Creep & Viscoplasticity Testing',
  '620.11296': 'Corrosion Testing',
  '620.11297': 'Surface Hardness Testing',
  '620.11299': 'Non-Destructive Evaluation Methods',

  // MOSFET deep (621.38104x)
  '621.381044': 'n-Channel MOSFET',
  '621.381045': 'p-Channel MOSFET',
  '621.381046': 'CMOS Technology',

  // Radar deep (621.38456)
  '621.38456': 'Synthetic Aperture Radar (SAR)',
  '621.38453': 'Pulse Radar Systems',

  // Decision Making sub-levels (658.403x)
  '658.4032': 'Decision Trees & Game Theory',
  '658.4033': 'Multi-Criteria Decision Analysis',
  '658.4034': 'Risk Analysis in Decision-Making',
  '658.4036': 'Group Decision Making',

  // Chemical reactor types (660.281x)
  '660.2812': 'Continuous-Flow Reactors',
  '660.2815': 'Batch Reactors',

  // Scholarships deep (378.1662x)
  '378.1662': 'Scholarship Administration',
  '378.1664': 'Graduate Fellowships',

  // Gyroscopes (531.38x)
  '531.381': 'Pendulums',
  '531.384': 'Gyroscopic Motion',

  // Programming languages sub-class (005.131)
  '005.131': 'Compilers & Language Translators',
  '005.136': 'Object-Oriented Languages',
  '005.138': 'Functional & Logic Languages',

  // Organic chemistry broader
  '547.9': 'Special Topics in Organic Chemistry',

  // Inorganic Chemistry (546.x)
  '546.0': 'Inorganic Chemistry — General Works',
  '546.8': 'Transition Elements',

  // Polymer Chemistry (547.84x)
  '547.84': 'Synthetic Polymers',
  '547.843': 'Polymerisation Reactions',
  '547.845': 'Thermoplastics',
  '547.848': 'Elastomers & Rubbers',

  // Analytical Chemistry (543.x)
  '543.0': 'Analytical Chemistry — General Works',
  '543.2': 'Quantitative Analysis',
  '543.42': 'UV-Visible Spectroscopy',
  '543.44': 'Infrared Spectroscopy',
  '543.45': 'NMR Spectroscopy',
  '543.65': 'Mass Spectrometry',
  '543.7': 'Electrochemical Analysis',
  '543.8': 'Chromatography',

  // Optics (535.x)
  '535.0': 'Optics — General Works',
  '535.32': 'Interference & Diffraction',
  '535.37': 'Spectroscopy (Optics)',

  // Acoustics (534.x)
  '534.0': 'Acoustics — General Works',
  '534.1': 'Vibration & Waves',
  '534.4': 'Ultrasound',

  // Water treatment deeper (628.16x)
  '628.161': 'Flocculation & Sedimentation',
  '628.166': 'Disinfection of Water',

  // Agricultural Engineering (631.x)
  '631.30': 'Farm Machinery — General',
  '631.31': 'Tillage Equipment',
  '631.33': 'Harvesting Equipment',
  '631.35': 'Planting Equipment',
  '631.37': 'Tractors',

  // Soil Science (631.4x)
  '631.40': 'Soil Science — General',
  '631.42': 'Soil Surveys & Mapping',
  '631.43': 'Physical Properties of Soil',
  '631.44': 'Chemical Properties of Soil',
  '631.45': 'Microbiology of Soil',

  // Crop Production (633.x)
  '633.10': 'Cereal Crops — General',
  '633.11': 'Wheat',
  '633.15': 'Corn & Maize',
  '633.18': 'Rice',
  '633.2': 'Forage Crops',
  '633.3': 'Legumes',
  '633.4': 'Root & Tuber Crops',
  '633.5': 'Fibre Crops',
  '633.6': 'Sugar Crops',

  // Animal Science (636.x)
  '636.10': 'Horses & Equestrian',
  '636.20': 'Cattle',
  '636.30': 'Sheep & Goats',
  '636.40': 'Pigs',
  '636.50': 'Poultry',
  '636.70': 'Dogs',
  '636.80': 'Cats',

  // Forestry (634.9x)
  '634.90': 'Forestry — General Works',
  '634.92': 'Silviculture',
  '634.95': 'Forest Management',
  '634.97': 'Forest Products',
  '634.98': 'Forest Conservation',

  // Ethics (174.x beyond 174.4)
  '174.28': 'Ethics of Research & Experiments',
  '174.96': 'Engineering Ethics',
  '174.962': 'Environmental Ethics',

  // Ancient Philosophy (180.x)
  '180.0': 'Ancient Philosophy — General Works',
  '183': 'Sophist & Socratic Philosophy',

  // Modern Philosophy (190.x)
  '190.0': 'Modern Western Philosophy — General',

  // ── Round-9: all remaining fixable chip keys ───────────────────

  // Sensation & Perception (153.8x)
  '153.83': 'Motor Skills & Reaction Time',
  '153.85': 'Visual & Auditory Perception',

  // Personality — Self (155.24)
  '155.24': 'Self-Concept & Identity',

  // Ramayana cantos (294.5921x)
  '294.59214': 'Kishkindha Kanda (Ramayana)',
  '294.59215': 'Sundara Kanda (Ramayana)',

  // Women — geographic (305.4092, 305.4095)
  '305.4092': 'Women — Historical Treatment',
  '305.4095': 'Women in Asia',

  // Working class sub-groups (305.568x)
  '305.5680': 'Working Class by Demographic',
  '305.5688': 'Working Class & Immigration',

  // Foreign relations (327.5x)
  '327.52': 'Foreign Relations of China',
  '327.54': 'Foreign Relations of India',

  // Computational linguistics (410.1x)
  '410.15': 'Mathematical Linguistics',
  '410.18': 'Computational Linguistics',

  // Sanskrit language (491.23, 491.25, 491.28)
  '491.23': 'Sanskrit Grammar',
  '491.25': 'Sanskrit Composition & Style',
  '491.28': 'Classical Sanskrit Texts',

  // Arithmetic (513.82, 513.83)
  '513.82': 'Fractions & Decimals',
  '513.83': 'Ratio & Proportion',

  // Mathematical physics (530.15x)
  '530.151': 'Physical Dimensions & Units',
  '530.152': 'Physical Constants',
  '530.155': 'Vector Methods in Physics',
  '530.156': 'Tensor Methods in Physics',
  '530.159': 'Special Topics in Mathematical Physics',

  // Solid-state optical & electronic properties (530.415, 530.417)
  '530.415': 'Electronic Structure of Solids',
  '530.417': 'Optical Properties of Solids',

  // Electromagnetism (537.22, 537.24)
  '537.22': 'Electrostatics',
  '537.24': 'Electric Currents & Circuits',

  // Kinetics sub-levels (541.361, 541.369)
  '541.361': 'Elementary Reaction Kinetics',
  '541.369': 'Enzyme Kinetics & Biocatalysis',

  // Crystal properties (548.81, 548.84, 548.85)
  '548.81': 'Optical Properties of Crystals',
  '548.84': 'Thermal Properties of Crystals',
  '548.85': 'Electrical Properties of Crystals',

  // Biochemistry (572.80, 572.88)
  '572.80': 'Biochemistry — General Works',
  '572.88': 'Molecular Genetics & Genomics',

  // Mental & Personality Disorders (616.890, 616.891, 616.898)
  '616.890': 'Mental Disorders — General Works',
  '616.891': 'Schizophrenia & Psychotic Disorders',
  '616.898': 'Personality & Behavioural Disorders',

  // Engineering design (620.0044, 620.0045)
  '620.0044': 'Engineering Design for Environment',
  '620.0045': 'Human Factors in Engineering Design',

  // Tensile/compressive testing (620.11232, 620.11233)
  '620.11232': 'Tensile Strength Testing',
  '620.11233': 'Compressive Strength Testing',

  // Power distribution (621.3102, 621.3104)
  '621.3102': 'Electrical Power Transmission Systems',
  '621.3104': 'Power Distribution Networks',

  // Multirate DSP (621.3825)
  '621.3825': 'Multirate Signal Processing',
  '621.3826': 'Filter Design',

  // Gears & linkages (621.811, 621.816)
  '621.811': 'Gears & Gear Drives',
  '621.816': 'Cams, Links & Mechanisms',

  // Machine tools (621.90, 621.91, 621.98, 621.99)
  '621.90': 'Machine Tools — General Works',
  '621.91': 'Metal Cutting & Machining Operations',
  '621.98': 'Special Metalworking Processes',
  '621.99': 'Special-Purpose Machine Tools',

  // Chemical engineering theory (660.01, 660.05)
  '660.01': 'Chemical Engineering — Theory',
  '660.05': 'Chemical Engineering — Journals',

  // Printing methods (686.224, 686.225)
  '686.224': 'Screen Printing',
  '686.225': 'Digital & Inkjet Printing',

  // Masonry (693.82, 693.85)
  '693.82': 'Plastering & Rendering',
  '693.85': 'Roofing & Waterproofing',

  // Architectural drawing (720.23, 720.28)
  '720.23': 'Architectural Drawing & Drafting',
  '720.28': 'Construction Technology for Architects',

  // World War II (940.53, 940.54)
  '940.53': 'World War II (1939–1945)',
  '940.531': 'Causes & Origins of WWII',
  '940.5316': 'Holocaust',
  '940.5318': 'Resistance Movements',
  '940.54': 'World War II — Military Operations',
  '940.542': 'Pacific Theater',
  '940.5421': 'Battle of Britain',
  '940.5425': 'Eastern Front',
  '940.5426': 'North African Campaign',
  '940.5427': 'Western Front & D-Day',

  // India ancient / regional (954.00, 954.01, 954.09)
  '954.00': 'Indian History — General & Standard Subdivisions',
  '954.01': 'Ancient India (to 1200 CE)',
  '954.09': 'India — Regional History',

  // ── Round-10: final targeted fixes ────────────────────────────

  // Mobile app development (005.2762, 005.2768)
  '005.2762': 'Android Application Development',
  '005.2768': 'iOS Application Development',

  // Operating Systems sub-levels (005.434, 005.437)
  '005.434': 'Unix & Linux Operating Systems',

  // Memory & Thinking (153.12, 153.13, 153.15)
  '153.13': 'Imagery & Mental Representation',
  '153.15': 'Problem Solving & Creative Thinking',

  // Social Science Research (300.72x)
  '300.721': 'Social Science Research Design',
  '300.722': 'Statistical Methods in Social Science',
  '300.723': 'Computing in Social Research',

  // Group & Organisational Communication (302.34, 302.35)
  '302.34': 'Small Group Communication',
  '302.35': 'Organisational Communication',

  // Women in specific countries (305.40952, 305.40954)
  '305.40952': 'Women in Japan & East Asia',
  '305.40954': 'Women in India',

  // Minority groups (305.48)
  '305.48': 'Specific Ethnic & Religious Groups',

  // Market structure (338.70, 338.74, 338.76)
  '338.70': 'Competition & Monopoly',
  '338.74': 'Oligopoly & Imperfect Competition',
  '338.76': 'Corporations & Business Enterprise',

  // Mental Health Services (362.20, 362.21, 362.28, 362.29)
  '362.20': 'Mental Health Services — General',
  '362.21': 'Psychiatric Hospitals',
  '362.28': 'Addiction Rehabilitation Services',
  '362.29': 'Crisis Intervention',

  // Safety hazards (363.11, 363.12, 363.17)
  '363.11': 'Occupational Safety & Industrial Hazards',
  '363.12': 'Transportation Safety',
  '363.17': 'Nuclear & Radiation Hazards',

  // Public utilities (363.61, 363.69)
  '363.61': 'Water Supply & Services',
  '363.69': 'Energy Services & Utilities',

  // Number Theory (512.75, 512.78)
  '512.75': 'Analytic Number Theory',
  '512.78': 'Algebraic Number Fields',

  // Linear Algebra deep (512.815, 512.817)
  '512.815': 'Matrices & Matrix Theory',
  '512.817': 'Eigenvalues, Eigenvectors & Spectral Theory',

  // Topology (514.72, 514.74)
  '514.72': 'Algebraic Topology',
  '514.74': 'Differential Topology',
  '514.742': 'Manifolds',

  // Analytical methods (543.085, 543.087, 543.089)
  '543.085': 'Chromatographic Techniques',
  '543.087': 'Electrochemical Analytical Methods',
  '543.089': 'Thermal Analytical Methods',

  // Human Physiology sub-levels (612.00, 612.01, 612.02, 612.04)
  '612.00': 'Human Physiology — General Works',
  '612.01': 'Physiology — Theoretical Basis',
  '612.02': 'Biophysics of Physiology',
  '612.04': 'Cellular & Molecular Physiology',

  // Pathology sub-levels (616.00, 616.04)
  '616.00': 'Medicine & Disease — General Works',
  '616.04': 'Molecular & Genetic Pathology',

  // Composite materials (620.191, 620.192)
  '620.191': 'Ceramics & Glass Materials',
  '620.192': 'Polymers, Plastics & Rubber',

  // DSP deep (621.38220, 621.38223)
  '621.38220': 'Discrete-Time Signal Processing',
  '621.38223': 'Wavelets & Multiresolution Analysis',

  // Air pollution control (628.50, 628.52)
  '628.50': 'Air Pollution Control — General',
  '628.52': 'Industrial Air Pollution Treatment',

  // Chemical industry (660.294, 660.299)
  '660.294': 'Chemical Plant Safety',
  '660.299': 'Chemical Industry — Special Topics',

  // Architectural theory (720.103, 720.108)
  '720.103': 'Theory of Architecture',
  '720.108': 'Architecture for Special Purposes',

  // Illustration deeper (741.602, 741.609)
  '741.602': 'Illustration Techniques & Media',
  '741.609': 'Illustration History by Country',

  // Theatre (792.02, 792.09)
  '792.02': 'Stage Production & Theatre Management',
  '792.09': 'Theatre History',

  // American Literature by theme (810.91, 810.93, 810.99)
  '810.91': 'American Literature — Historical Criticism',
  '810.93': 'American Literature — Themes & Movements',
  '810.99': 'American Literature — Special Groups',

  // Italian Literature (853.91, 853.92)
  '853.91': 'Italian Fiction — 20th Century',
  '853.92': 'Contemporary Italian Fiction',

  // India — states (954.13, 954.14, 954.16)
  '954.13': 'Rajasthan',
  '954.14': 'Uttar Pradesh',
  '954.16': 'Madhya Pradesh',
  '954.17': 'Bihar',
  '954.18': 'West Bengal',
  '954.19': 'Assam & Northeast India',

  // ── Round-11: final 67 fixable chip keys ──────────────────────

  // Software platforms (005.26x)
  '005.262': 'Middleware & Enterprise Software',
  '005.265': 'Embedded & Firmware Software',
  '005.268': 'Cross-Platform & Portable Software',

  // Hindu Philosophy sub-levels (294.543x)
  '294.5430': 'Vedanta — General Works',
  '294.5435': 'Advaita Vedanta',
  '294.5436': 'Vishishtadvaita & Dvaita Vedanta',

  // Race studies (305.800x)
  '305.8001': 'Race Theory & Philosophy',
  '305.8007': 'Race & Ethnic Studies Education',

  // Sexual relations & family (306.70, 306.73, 306.74)
  '306.70': 'Sex & Society — General Works',
  '306.73': 'Premarital & Extramarital Relations',
  '306.74': 'Prostitution & Sex Work',

  // Family welfare services (362.82, 362.88)
  '362.82': 'Child Abuse & Child Welfare',
  '362.88': 'Victims of Violent Crime — Services',

  // Criminology (364.15, 364.16)
  '364.15': 'Crimes Against Persons',
  '364.16': 'Crimes Against Property',

  // Tensor algebra (512.97)
  '512.97': 'Category Theory',

  // Physics research methods (530.028, 530.029)
  '530.028': 'Auxiliary Techniques in Physics Research',
  '530.029': 'Physics — Commercial Applications',

  // Statistical mechanics (530.132, 530.138)
  '530.132': 'Classical Statistical Mechanics',
  '530.138': 'Quantum Statistical Mechanics',

  // Newtonian mechanics (531.1133, 531.1134)
  '531.1133': 'Newton\'s Second Law & Force',
  '531.1134': 'Conservation of Energy (Classical)',

  // Rigid body mechanics (531.32, 531.33, 531.34)
  '531.32': 'Rigid Body Rotation',
  '531.33': 'Moments of Inertia',
  '531.34': 'Angular Momentum',

  // Biology quantitative (570.1511, 570.1519)
  '570.1511': 'Biostatistics',
  '570.1519': 'Computational Biology Methods',

  // Medicine history & geography (610.92, 610.95)
  '610.92': 'Physicians (Biography)',
  '610.95': 'Medicine in Asia',

  // Paediatrics (618.920, 618.928, 618.929)
  '618.920': 'Paediatrics — General Works',
  '618.928': 'Paediatric Neurology',
  '618.929': 'Neonatology & Newborn Medicine',

  // Materials science sub-levels (620.101, 620.103, 620.104)
  '620.101': 'Mechanics of Materials — Theory',
  '620.103': 'Elasticity of Materials',
  '620.104': 'Plasticity & Creep',

  // General Mechanical Engineering (621.01, 621.04)
  '621.01': 'Mechanical Engineering — Theory',
  '621.04': 'Mechanical Engineering — Special Topics',

  // Integrated Circuits deep (621.38153x)
  '621.381531': 'Bipolar Integrated Circuits',
  '621.381532': 'CMOS Integrated Circuits',
  '621.381533': 'Mixed-Signal ICs',
  '621.381537': 'RF & Microwave Integrated Circuits',

  // Career & self-help (650.11, 650.14)
  '650.11': 'Success in Business & Career',
  '650.14': 'Career Development & Job Seeking',

  // CNC & automation (670.427)
  '670.427': 'Computer Numerical Control (CNC)',

  // Construction technology for architects (720.284, 720.285, 720.288)
  '720.284': 'Structural Systems for Architects',
  '720.285': 'Building Materials for Architects',
  '720.288': 'Sustainable Building & Green Architecture',

  // Religious architecture (726.10, 726.14)
  '726.10': 'Religious Architecture — General',
  '726.14': 'Hindu & Buddhist Temples',
  '726.12': 'Churches & Christian Architecture',
  '726.16': 'Mosques & Islamic Architecture',

  // Film criticism deeper (791.43023, 791.43028)
  '791.43023': 'Film Theory & Semiotics',
  '791.43028': 'Film Technology & Cinematography',

  // English literature 1900– (828.912, 828.914)
  '828.912': 'English Prose — Early 20th Century',
  '828.914': 'English Prose — Mid-20th Century',

  // German fiction 1900– (833.912, 833.914)
  '833.912': 'German Fiction — Early 20th Century',
  '833.914': 'German Fiction — Weimar & WWII Era',

  // Scandinavian fiction (839.737, 839.738)
  '839.737': 'Swedish Fiction',
  '839.738': 'Norwegian Fiction',

  // French fiction 1900– (843.912, 843.914)
  '843.912': 'French Fiction — Early 20th Century',
  '843.914': 'French Fiction — Mid-20th Century',

  // India — ancient sub-periods (954.050, 954.052, 954.053)
  '954.050': 'Indus Valley & Vedic Period',
  '954.052': 'Mauryan Empire',
  '954.053': 'Gupta Empire',

  // ── Round-12: final 59 fixable keys ───────────────────────────

  // Computer graphics (006.67, 006.69)
  '006.67': '3D Computer Graphics & Modelling',
  '006.69': 'Animation & Rendering',

  // Library operations (025.00, 025.04)
  '025.00': 'Library Operations — General',
  '025.04': 'Digital Library Services',

  // Thought & Reasoning (153.43, 153.44)
  '153.43': 'Concept Formation',
  '153.44': 'Deductive & Inductive Reasoning',

  // Hindu Ritual sub-levels (294.5512, 294.5513, 294.5514)
  '294.5512': 'Daily Worship (Nitya Puja)',
  '294.5513': 'Occasional Rites & Samskaras',
  '294.5514': 'Festival Rituals (Utsava)',

  // Propaganda & influence (303.372, 303.375)
  '303.372': 'Mass Media & Propaganda',
  '303.375': 'Disinformation & Influence Operations',

  // Specific ethnic groups (305.891, 305.896, 305.899)
  '305.891': 'South Asian Communities',
  '305.896': 'African & African-Diaspora Communities',
  '305.899': 'Pacific Islanders & Indigenous Peoples',

  // Law enforcement (363.31, 363.32)
  '363.31': 'Police & Law Enforcement',
  '363.32': 'Military Forces & Defence',

  // Educational psychology (370.152, 370.157)
  '370.152': 'Motivation in Education',
  '370.157': 'Creativity in Education',

  // Functional Analysis deep (515.732, 515.733)
  '515.732': 'Measure Theory & Integration',
  '515.733': 'Sobolev Spaces',

  // Probability sub-levels (519.202, 519.207)
  '519.202': 'Probability — Mathematical Foundations',
  '519.207': 'Probability — Statistical Applications',

  // Phase transitions deeper (530.427, 530.429)
  '530.427': 'Ferromagnetism & Magnetic Phase Transitions',
  '530.429': 'Quantum Phase Transitions',

  // Catalysis deep (541.3451, 541.3453)
  '541.3451': 'Heterogeneous Catalysis',
  '541.3453': 'Enzyme Catalysis Mechanisms',

  // Chemical Kinetics vs Thermochemistry (541.35)
  '541.35': 'Photochemical Kinetics',

  // Cancer sub-types (616.99406, 616.99407)
  '616.99406': 'Breast Cancer',
  '616.99407': 'Lung Cancer',

  // Engineering general (620.002, 620.007, 620.009)
  '620.002': 'Engineering — Reference Works',
  '620.007': 'Engineering Education',
  '620.009': 'History of Engineering',

  // Power generation deep (621.312423, 621.312424, 621.312429)
  '621.312423': 'Three-Phase AC Generators',
  '621.312424': 'Synchronous Generators',
  '621.312429': 'Generator Control Systems',

  // Aerodynamics deep (629.1341, 629.1343)
  '629.1341': 'Subsonic Aerodynamics',
  '629.1343': 'Supersonic & Hypersonic Aerodynamics',

  // Automotive engineering (629.24)
  '629.24': 'Vehicle Dynamics & Chassis',
  '629.231': 'Automotive Engines',
  '629.246': 'Automotive Transmissions & Brakes',

  // Architecture history (720.94, 720.95)
  '720.94': 'European Architecture',
  '720.95': 'Asian Architecture',

  // World Literature by theme (809.9332, 809.9335)
  '809.9332': 'War Literature',
  '809.9335': 'Science Fiction & Fantasy',

  // American Fiction themes deep (813.0873, 813.0876)
  '813.0873': 'American Detective & Mystery Fiction',
  '813.0876': 'American Science Fiction',

  // Urdu Poetry (891.451, 891.453, 891.456)
  '891.451': 'Classical Urdu Poetry (Ghazal & Qasida)',
  '891.453': 'Modern Urdu Poetry',
  '891.456': 'Urdu Nazm & Free Verse',

  // Bengali Fiction deep (894.81234, 894.81237)
  '894.81234': 'Bengali Short Stories (20th C.)',
  '894.81237': 'Contemporary Bengali Fiction',

  // Japanese Fiction (895.634, 895.636)
  '895.634': 'Japanese Fiction — 20th Century',
  '895.636': 'Contemporary Japanese Fiction',

  // World History by period (909.82, 909.83)
  '909.82': 'World History — 20th Century',
  '909.83': 'World History — 21st Century',

  // Geography (910.20, 910.28)
  '910.20': 'Geography — General Works',
  '910.28': 'Geography — Research Methods & Tools',

  // India — standard subdivisions deep (954.003, 954.005, 954.007)
  '954.003': 'India — Dictionaries & Encyclopedias',
  '954.005': 'India — Journals & Serials',
  '954.007': 'India — Education & Research',

  // ── Round-13: penultimate fixable keys ────────────────────────

  // Child Psychology sub-levels (155.412, 155.418)
  '155.412': 'Infant & Toddler Development',
  '155.418': 'Middle Childhood Development',

  // Vedic Literature sub-levels (294.536, 294.538)
  '294.536': 'Vedantic Commentary Literature',
  '294.538': 'Agamas & Tantric Texts',

  // Technology & AI in society deep (303.4832, 303.4834)
  '303.4832': 'Computers & Digital Technology in Society',
  '303.4834': 'Automation & Robots in Society',

  // Social Classes (305.50, 305.55)
  '305.50': 'Social Classes — General Works',
  '305.55': 'Professional Class & Intelligentsia',

  // Production Economics history (338.092, 338.095)
  '338.092': 'Economists (Biography)',
  '338.095': 'Production Economics of Asia',

  // Drug & Alcohol Services (362.40, 362.41)
  '362.40': 'Drug Services — General Works',
  '362.41': 'Drug Abuse Treatment & Rehabilitation',

  // Disaster response sub-levels (363.340, 363.349)
  '363.340': 'Disaster Management — General',
  '363.349': 'Recovery & Reconstruction',

  // Education & Society (370.113, 370.115)
  '370.113': 'Multicultural Education',
  '370.115': 'Global Citizenship Education',

  // Education Research (370.72, 370.78)
  '370.72': 'Educational Research Methods',
  '370.78': 'Action Research in Education',

  // Applied Mathematics (510.21, 510.28)
  '510.21': 'Applied Mathematics — Theory',
  '510.28': 'Applied Mathematics — Instruments & Techniques',

  // ODE & PDE sub-levels (515.33, 515.37)
  '515.33': 'Sturm-Liouville Problems',
  '515.37': 'Variational Problems & Calculus of Variations',

  // Complex Analysis (517.50, 517.52)
  '517.50': 'Complex Analysis — General Works',
  '517.52': 'Holomorphic Functions & Complex Mappings',

  // Statistical Theory sub-levels (519.5024, 519.5028)
  '519.5024': 'Estimation Theory',
  '519.5028': 'Hypothesis Testing & Significance',

  // Meteorology sub-levels (551.60, 551.63)
  '551.60': 'Meteorology — General Works',
  '551.63': 'Numerical Weather Prediction',

  // Life Sciences — ecology (574.19)
  '574.19': 'Life Sciences — Ecology & Environments',

  // Chimpanzees & apes (599.938)
  '599.938': 'Chimpanzees & Gorillas',

  // Physical Therapy (615.84, 615.85)
  '615.84': 'Physical & Occupational Therapy',
  '615.85': 'Therapeutic Exercises & Rehabilitation',

  // Engineering mathematics deep (620.0011)
  '620.0011': 'Mathematical Modelling in Engineering',

  // Refrigeration & power (621.55, 621.58)
  '621.55': 'Cryogenic Engineering',
  '621.58': 'Vacuum Technology',

  // Steel beams deep (624.183412, 624.183414)
  '624.183412': 'Steel I-Beams & Wide-Flange Sections',
  '624.183414': 'Steel Hollow Sections & Tubes',

  // Automotive (629.222, 629.228, 629.229)
  '629.222': 'Passenger Car Engineering',
  '629.228': 'Electric & Hybrid Vehicles',
  '629.229': 'Commercial Vehicle Engineering',

  // Corporate Finance sub-levels (658.155, 658.159)
  '658.155': 'Capital Budgeting',
  '658.159': 'International Corporate Finance',

  // Art philosophy (701.03, 701.05)
  '701.03': 'Philosophy of Art & Beauty',
  '701.05': 'Art Criticism & Theory',

  // Decorative arts (745.206, 745.209)
  '745.206': 'Decorative Arts Organisations',
  '745.209': 'History of Decorative Arts',

  // Theatre by country (792.094, 792.095)
  '792.094': 'European Theatre',
  '792.095': 'Asian Theatre',

  // English Poetry (821.008, 821.009)
  '821.008': 'English Poetry — Anthologies',
  '821.009': 'English Poetry — Historical Treatment',

  // Hindi Fiction further (891.4301, 891.4309)
  '891.4301': 'Hindi Fiction — 19th Century',
  '891.4309': 'Hindi Fiction — Geographic Treatment',

  // Deep Marathi cascade (library-specific very long callnums)
  '894.827301083': 'Marathi Short Stories — Specific Authors',
  '894.827301089': 'Marathi Short Stories — Other',
  '894.827301089282': 'Marathi Short Story Anthology',
  '894.827301089287': 'Marathi Short Stories — Award Collections',

  // ── Round-14: exhaustive final batch ──────────────────────────

  // Personal computer history (001.6424, 001.6425)
  '001.6424': 'Personal Computers — Hardware History',
  '001.6425': 'Operating Systems History',

  // Linux & macOS (005.446, 005.447)
  '005.446': 'Linux & Open-Source Operating Systems',
  '005.447': 'macOS & Apple Operating Systems',

  // Speech recognition (006.45)
  '006.45': 'Speech Recognition & Synthesis',

  // 3D rendering (006.693, 006.696)
  '006.693': 'Ray Tracing & Rendering Algorithms',
  '006.696': 'GPU & Shader Programming',

  // Vedic scripture (294.521, 294.522)
  '294.521': 'Rig Veda Samhita',
  '294.522': 'Sama & Yajur Veda Samhitas',

  // Sociology history (301.092, 301.097)
  '301.092': 'Sociologists (Biography)',
  '301.097': 'Sociology in North America',

  // Culture & institutions (306.01, 306.07)
  '306.01': 'Philosophy of Cultural Studies',
  '306.07': 'Cultural Studies Education',

  // Human rights (323.092, 323.095)
  '323.092': 'Human Rights Advocates (Biography)',
  '323.095': 'Human Rights in Asia',

  // Service industries deeper (338.473, 338.476, 338.479)
  '338.473': 'Retail Trade',
  '338.476': 'Financial Services Industry',
  '338.479': 'Information & Technology Services',

  // International Human Rights Law (341.48)
  '341.48': 'International Human Rights Law',

  // Water supply in Asia (363.61095, 363.61099)
  '363.61095': 'Water Services in Asia',
  '363.61099': 'Water Services — Other Regions',

  // Educational methods (371.394, 371.397)
  '371.394': 'Cooperative & Project-Based Learning',
  '371.397': 'Problem-Based Learning',

  // Mathematical Logic (511.31)
  '511.31': 'Propositional & Predicate Logic',

  // Ring Theory (512.62, 512.64)
  '512.62': 'Commutative Rings',
  '512.64': 'Non-Commutative Rings',

  // Integral Equations sub-levels (515.55, 515.56)
  '515.55': 'Fredholm Integral Equations',
  '515.56': 'Volterra Integral Equations',

  // Geometry sub-levels (516.07, 516.08)
  '516.07': 'Geometry Education',
  '516.08': 'Geometry for Specific Groups',

  // Euclidean Geometry (516.22, 516.24)
  '516.22': 'Plane Euclidean Geometry',
  '516.24': 'Solid Euclidean Geometry',

  // Newton's Laws deep (531.01515, 531.01516)
  '531.01515': 'Newton\'s First Law (Inertia)',
  '531.01516': 'Newton\'s Third Law (Action-Reaction)',

  // Organic chemistry methods (547.0076, 547.0078)
  '547.0076': 'Organic Chemistry — Exercises',
  '547.0078': 'Organic Chemistry — Labs',

  // Hydrology sub-levels (551.480, 551.483, 551.489)
  '551.480': 'Hydrology — General Works',
  '551.483': 'River & Stream Hydrology',
  '551.489': 'Groundwater Hydrology',

  // Materials science deeper (620.1186, 620.1189)
  '620.1186': 'Viscoelastic Properties',
  '620.1189': 'Smart & Functional Materials',

  // Manufacturing materials (620.23)
  '620.23': 'Concrete & Cement in Engineering',

  // Power transformer deep (621.31922, 621.31924)
  '621.31922': 'Distribution Transformers',
  '621.31924': 'Power Transformer Design',

  // Aerodynamics deep (629.13232, 629.13236, 629.13237)
  '629.13232': 'Boundary Layer & Viscous Aerodynamics',
  '629.13236': 'Computational Aerodynamics (CFD)',
  '629.13237': 'Wind Tunnel Testing',

  // Management (658.007, 658.009)
  '658.007': 'Management Education & Training',
  '658.009': 'History of Management',

  // Customer Service & CRM (658.85)
  '658.85': 'Customer Service Management',

  // Chemical technology (660.2994, 660.2995)
  '660.2994': 'Chemical Process Optimisation',
  '660.2995': 'Chemical Process Simulation',

  // Music (780.92, 780.95)
  '780.92': 'Musicians & Composers (Biography)',
  '780.95': 'Music in Asia',

  // American Fiction sub-genres (813.540, 813.542)
  '813.540': 'American Fiction — 20th-Century Collections',
  '813.542': 'American Fiction — Specific 20th-C. Themes',

  // ── Added Popular Missing DDC Classifications ──
  '621.4021': 'Engineering Thermodynamics',
  '820.9': 'English Literature — History & Criticism',
  '741.59493': 'Belgian Comic Books',
  '621.31042': 'Electrical Machinery',
  '541.28': 'Quantum Chemistry',
  '615.19': 'Pharmaceutical Chemistry',
  '658.5752': 'New Product Development',
  '810.9': 'American Literature — History & Criticism',
  '305.51220954': 'Caste System in India',
  '572.80285': 'Bioinformatics',
  '305.56880954': 'Dalit Studies / Social Classes in India',
  '307.760954': 'Urbanization in India',
  '330.015195': 'Econometrics',
  '330.122': 'Capitalism',
  '332.042': 'International Finance',
  '398.20954': 'Indian Folklore',
  '510.1': 'Philosophy & Foundations of Mathematics',
  '512.897': 'Linear Algebra',
  '794.8': 'Computer & Video Games',
  '820.8': 'English Literature Anthologies',
  '004.36': 'Distributed Computing',
  '342.54': 'Constitutional Law of India',
};

export default DDC_NAMES;
